import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Spinner, Alert } from 'react-bootstrap';
import DesktopLayout from '../layouts/DesktopLayout';
import { AuthContext } from '../context/AuthContext';
import { notificationApi } from '../services/notificationApi';
import { getStoredDeviceId } from '../utils/serviceWorker';
// Imported, never re-declared — roleConfig.js exists precisely so components stop hardcoding role
// arrays, and this one gates a privileged action.
import { SUPERVISOR_ROLES } from '../config/roleConfig';
import { conversationApi, isEndpointMissing, newIdempotencyKey, LIST_LIMIT } from '../services/conversationApi';
import BookingRefLine from '../components/BookingRefLine';
import ThreadContextPanel from '../components/inbox/ThreadContextPanel';
import RepeatGuestBadge from '../components/inbox/RepeatGuestBadge';
import MessageAttachment, { AttachmentLabel } from '../components/inbox/MessageAttachment';
import WhatsAppThreadBar from '../components/inbox/WhatsAppThreadBar';
import WhatsAppFile from '../components/inbox/WhatsAppFile';
import SentPhoto from '../components/inbox/SentPhoto';
import ErrorBoundary from '../components/ErrorBoundary';
import RelocationBanner from '../components/RelocationBanner';
import useRelocations from '../hooks/useRelocations';
import { formatRelativeTime, formatShortDateTime, formatShortDate } from '../utils/dateFormatter';
import { getPlatformDisplayName, getPlatformLogo } from '../utils/platformUtils';
// The shadow evaluator's Arabic vocabulary. In `config/` rather than inline, per the five existing
// label modules — agreeing on the codes while retyping the words is invisible in a diff.
import {
  SHADOW_INTENTS,
  SHADOW_CONTRADICTIONS,
  SHADOW_SAFE_TO_AUTOMATE,
  SHADOW_SAFE_TO_AUTOMATE_SILENT,
  SHADOW_UNSAFE_REASONS,
  SHADOW_SHOULD_HAVE_ANSWERED_REASONS,
  SHADOW_QUESTIONS,
  SHADOW_DRAFT_NOTE,
  shadowScopeLine,
  SHADOW_SAVE_ERRORS,
  SHADOW_STABILITY,
  SHADOW_OVERNIGHT_TAG,
  SHADOW_COLLAPSED,
  shadowReasonAr
} from '../config/enquiryShadowLabels';
import '../styles/InboxPage.css';
import { STAY_CHECK_PROMPT } from '../config/stayCheckLabels';

// Unified inbox — Guest Comms stage 1, Inc 3 (read) + Inc 4 (reply). No claim, no mark-read yet.
// Spec + locked decisions: tasks/guest-comms-stage1-unified-inbox.md
//
// OPTIMISTIC BY NECESSITY, not by preference. This page has no auto-refresh and no polling (an
// accepted Inc 3 limitation), so a sent reply that is not painted immediately would simply not
// appear until the operator reopened the thread — which reads as a message that vanished. Every
// send therefore renders locally the instant it is accepted, and carries its own visible status.
//
// Reads the Conversation projection + the ota_messages ledger through /api/v1/conversations.
// Arabic-only (no t()), RTL, per the Arabic-first mandate.
//
// RENDERING SAFETY (G3): guest text is attacker-influencable and the API returns it AS STORED,
// unsanitised — deliberately, so the text is not corrupted for other consumers. It is made safe
// HERE, and the mechanism is simply that every message goes through normal JSX interpolation.
// React escapes it. There is no dangerouslySetInnerHTML anywhere in this file, and adding one
// would reintroduce the exact hole this boundary exists to close. Newlines are preserved with
// CSS `white-space: pre-wrap`, never by splitting text into markup.

// D75's «سجل المحادثات يبدأ 1 يوليو 2026» notice was REMOVED 2026-08-01 on Adil's instruction —
// ops are told about the forward-only history gap directly, so the banner was noise on every short
// thread. The gap itself is unchanged; only the on-screen label is gone. Do not reinstate without
// asking: this reverses a locked decision deliberately.

// Enough to hold any thread we have (largest is far below this), so the common case is one request.
const THREAD_PAGE_SIZE = 200;

const FILTERS = [
  { key: 'all', label: 'الكل' },
  { key: 'needsReply', label: 'بانتظار الرد' },
  { key: 'unowned', label: 'غير مسندة' }
];
// «ليس ضيفاً» chats, so a supervisor can undo a wrong press. Offered only while WhatsApp is on.
const HIDDEN_FILTER = { key: 'hidden', label: 'مخفية' };
const WHATSAPP_FILTER = { key: 'whatsapp', label: getPlatformDisplayName('whatsapp') };
// Everything that isn't WhatsApp, so offered only beside it: with WhatsApp off this would just repeat «الكل».
const OTA_FILTER = { key: 'ota', label: 'منصات الحجز' };

// One wording for one state, matching the API's. Three near-identical phrasings had grown across the
// page and the route, which reads as three different problems to anyone who meets more than one.
const REPLY_DISABLED = 'الرد من النظام غير مفعّل حالياً';

// Inc 8 (D131). Real usage is ~1-2 photos a day across two operators, so this is conservative by
// design and trivially raised — an unbounded picker is how a runaway multi-select turns one compose
// action into forty vendor calls. The server does not enforce a per-action count (it only ever sees
// one photo per request), so this cap is the client's alone; the rate limiter is the backstop.
const MAX_REPLY_PHOTOS = 3;

// A quiet re-read of the list, so one person's claim becomes visible to the rest of the team without
// anyone reloading the page. Ownership that only updates on refresh is ownership nobody can rely on —
// and this is also what surfaces a colleague's new message, and what makes the composer collision
// warning possible at all. Silent, so it never blanks a list someone is reading.
const LIST_REFRESH_MS = 30000;

/**
 * Where the conversation is about. Our own unit when a booking has attributed the thread; failing
 * that, our own unit as resolved from the enquiry's room-type id; failing that, the OTA's listing
 * title, which is the platform's words rather than our unit — so it is rendered as a plain label
 * with no property code beside it. «غير محدد» only when we have none of the three, which now means
 * an enquiry captured before the `inquiry` triggers were subscribed, or one whose room type we
 * could not resolve to exactly one unit.
 *
 * ORDER MATTERS: the booking always wins — as it does for the guest's name — because it is our own
 * record and follows alterations, whereas the enquiry is a one-shot snapshot.
 *
 * Guarded on `.name`, not on the object: both fields arrive POPULATED today, but a truthy-object
 * check would also pass for a bare ObjectId, and that renders an empty label — skipping both the
 * listing title and «غير محدد» — rather than falling through. Cheap insurance now that two separate
 * fields feed this.
 */
const unitLabel = (c) => {
  // `suggestedBooking` is a WhatsApp phone match: good enough to label a row, and the bar under the thread
  // header says it is only a match.
  const p = [c.matchedPropertyId, c.enquiryPropertyId, c.suggestedBooking?.property].find((x) => x?.name);
  if (p?.name) return p.propertyCode ? `${p.propertyCode} · ${p.name}` : p.name;
  return c.listingName || (isWhatsApp(c) ? null : 'غير محدد');
};

// Left-to-right, or the plus sign lands on the wrong end of the number in this RTL page.
const whatsappNumber = (c) => (c.contactPhone ? <bdi dir="ltr">{c.contactPhone}</bdi> : null);

/** Name comes from the booking when attributed, else from the captured enquiry. */
const guestLabel = (c) => c.guestName || whatsappNumber(c) || 'ضيف غير محدد';

const isWhatsApp = (c) => c?.channel === 'whatsapp';

// The unit, plus a WhatsApp number when a name took the title. The number stays visible because a name may be wrong.
const contactLine = (c, stay = null) => [unitLabel(c), c.guestName ? whatsappNumber(c) : null, stay]
  .filter(Boolean)
  .map((part, i) => <React.Fragment key={i}>{i > 0 && ' · '}{part}</React.Fragment>);

// A thread whose platform we don't know ('unknown') has no logo, so nothing is drawn: never a guessed badge.
const ChannelLogo = ({ conversation }) => {
  const logo = getPlatformLogo(conversation?.channel);
  return logo ? <img className="inbox-channel-logo" src={logo} alt={getPlatformDisplayName(conversation.channel)} /> : null;
};

// A WhatsApp message we can't show here says what it is, rather than reading as a blank bubble.
const WHATSAPP_TYPE_LABELS = {
  image: 'صورة', audio: 'رسالة صوتية', voice: 'رسالة صوتية', video: 'فيديو', document: 'مستند',
  location: 'موقع', sticker: 'ملصق', contact: 'جهة اتصال'
};
const whatsappTypeLabel = (type) => WHATSAPP_TYPE_LABELS[type] || 'نوع رسالة غير مدعوم هنا';
const whatsappTypeHint = (type) => `${whatsappTypeLabel(type)}: للعرض افتح ${getPlatformDisplayName('whatsapp')} على جوال العمليات`;

/**
 * Where to go to see an attachment. Named from the thread's own channel — `channel` can be 'unknown'
 * (the message webhook carries no provider field, G6), and asserting a platform we cannot evidence is
 * worse than staying vague.
 */
const attachmentHint = (c) => {
  const channel = c?.channel;
  if (!channel || channel === 'unknown') return 'مرفق — افتح المحادثة في تطبيق المنصة لعرضه';
  return `مرفق — افتح المحادثة في تطبيق ${getPlatformDisplayName(channel)} لعرضه`;
};

/**
 * WHICH automation sent this bubble (D112). The server sends the machine name; the Arabic belongs
 * here, on the only screen that renders it — the API stays language-neutral by convention
 * (I18N_STRATEGY.md → "API responses are NOT localized").
 *
 * An unrecognised type falls back to the generic «رسالة تلقائية» rather than printing a raw enum:
 * if a fifth automation is added server-side before this map learns it, the operator sees something
 * true but vague instead of `checkin_photo_3`.
 */
const AUTOMATED_LABELS = {
  checkin: 'رسالة تسجيل الدخول',
  checkin_photo_1: 'صورة الوحدة',
  checkin_photo_2: 'صورة الوحدة',
  stay_check: 'رسالة الاطمئنان',
  checkout: 'تذكير المغادرة',
  // Sent from the reviews page by a colleague (tasks/review-sends-in-inbox-thread.md) — the label names
  // the colleague too, since a person wrote or approved these, unlike the scheduled ones above.
  review_thankyou: 'رسالة شكر بعد المغادرة',
  review_followup: 'رسالة خاصة بعد التقييم'
};
const automatedLabel = (type) => AUTOMATED_LABELS[type] || 'رسالة تلقائية';



/**
 * The booking reference line — code · platform — shared with both readiness cards
 * (`components/BookingRefLine.js`), never re-implemented here.
 *
 * Returns null rather than the component's own «—» placeholder when there is no code: an enquiry
 * thread has no booking at all, and a dash reads as "a reference we failed to load" instead of
 * "there is nothing to reference". Those rows already show `listingName`.
 *
 * `channel` can be 'unknown' (no webhook carries a provider — G6; a few old threads on prod never
 * got one), so it is passed as UNDEFINED, not the string: the component omits a falsy platform,
 * and printing "unknown" would be asserting something we do not know.
 */
const bookingRef = (c, { linked }) => {
  if (!c.confirmationCode) return null;
  return (
    <BookingRefLine
      confirmationCode={c.confirmationCode}
      platform={c.channel && c.channel !== 'unknown' && !isWhatsApp(c) ? c.channel : undefined}
      // A WhatsApp suggestion links too, so staff can look at the booking before confirming it. The door code stays
      // out of the chat until they do.
      bookingId={linked ? (c.matchedBookingId || (isWhatsApp(c) ? c.suggestedBooking?.bookingId : undefined)) : undefined}
    />
  );
};

const stayLabel = (c) => {
  if (!c.checkIn || !c.checkOut) return null;
  // Date-only formatter: these are BUSINESS DATES with a non-uniform anchor, never instants.
  return `${formatShortDate(c.checkIn)} – ${formatShortDate(c.checkOut)}`;
};

// An on-screen keyboard has no Shift, so "Shift+Enter for a newline" is a DESKTOP-only escape
// hatch — on a phone, Enter is the only newline the operator has, and sending on it turned one
// reply into three separate messages to the guest (reported 2026-08-03). `pointer: coarse` asks the
// honest question ("is the primary input a finger", i.e. is the keyboard on-screen) instead of
// guessing from width, which mislabels both a narrow desktop window and a landscape phone.
const TOUCH_KEYBOARD_QUERY = '(pointer: coarse)';

const useTouchKeyboard = () => {
  const [touch, setTouch] = useState(
    () => window.matchMedia?.(TOUCH_KEYBOARD_QUERY).matches === true
  );
  useEffect(() => {
    const mq = window.matchMedia?.(TOUCH_KEYBOARD_QUERY);
    if (!mq) return undefined;
    // Re-evaluated rather than read once: a tablet gaining or losing a keyboard changes the answer
    // mid-session, and the composer would otherwise keep the behaviour of the device it loaded on.
    const onChange = (e) => setTouch(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return touch;
};

const InboxPage = () => {
  // Same triple fallback AlertsManagement uses — the shape of `user` differs by how the session was
  // established, and getting it wrong would silently render every thread as somebody else's.
  const { user } = useContext(AuthContext);
  const touchKeyboard = useTouchKeyboard();
  const currentUserId = user?.userId || user?._id || user?.id || null;
  const isSupervisor = SUPERVISOR_ROLES.includes(user?.role);
  // One definition, used by the row and the thread header — they had drifted into two spellings of
  // the same comparison, which is how the two surfaces end up disagreeing about whose thread it is.
  const isOwnedByMe = useCallback(
    (owner) => !!(owner?._id && currentUserId && String(owner._id) === String(currentUserId)),
    [currentUserId]
  );

  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);          // server-side total, NOT conversations.length
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [filter, setFilter] = useState('all');

  // Which filter the rows currently in `conversations` were actually fetched under, or null when the
  // last fetch failed. Distinct from `filter`, which is the CHOICE and changes before the data does.
  const [loadedFilter, setLoadedFilter] = useState(null);

  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  // WHICH bubbles are currently showing the guest's own words instead of Airbnb's Arabic (D114).
  // Keyed by message id, so the toggle is PER MESSAGE and not per thread: a mixed thread — some
  // Arabic, some English — is the normal case, and one switch for the whole conversation would flip
  // messages that were never translated in the first place.
  // Default empty = Arabic first, which is the decision. Cleared with the thread, so it cannot grow
  // across a shift at the desk.
  const [showOriginal, setShowOriginal] = useState({});
  const [olderMessagesExist, setOlderMessagesExist] = useState(false);

  // ---- Inc 4: reply ----
  // Told to us by the server, never guessed: there is no REACT_APP_ flag because CRA bakes those at
  // build time, so one could not be flipped without a rebuild. Defaults to false so a frontend
  // deployed ahead of the backend shows the read-only bar rather than a composer that 403s.
  const [replyEnabled, setReplyEnabled] = useState(false);
  // WhatsApp only, told by the server: the 24h rule for the open thread, whether WhatsApp is on at all, and
  // whether its feed has gone quiet.
  const [whatsappWindow, setWhatsappWindow] = useState(null);
  const [whatsappInboxEnabled, setWhatsappInboxEnabled] = useState(false);
  const [whatsappFeed, setWhatsappFeed] = useState(null);
  const [draft, setDraft] = useState('');
  // Keys currently in flight, NOT one page-level boolean. A boolean disabled the composer — and the
  // retry button on every unrelated bubble — for up to 35 seconds, across every thread.
  const [sendingKeys, setSendingKeys] = useState(() => new Set());
  // Tracked separately from the key set: the COMPOSER should wait only on a new message it is
  // sending, never on a retry of some earlier bubble that happens to be in flight.
  const [composerBusy, setComposerBusy] = useState(false);
  // Replies made from this browser, KEYED BY CONVERSATION and deliberately NOT cleared on a thread
  // switch. Each carries the idempotency key it was created with, and that key is the only thing
  // stopping a retry from posting the guest a second copy. Clearing on switch meant: send → open
  // another thread → come back → the bubble and its key are gone → the operator retypes → a NEW key
  // → the guest gets it twice. Confirmed sends ARE dropped when a thread is reopened, because from
  // then on the server returns them as ordinary merged bubbles and keeping both renders each twice.
  const [outgoing, setOutgoing] = useState({});

  // ---- Inc 8: photo replies (D123-D132) ----
  // A CAPABILITY the server resolves, never inferred here: only a conversation with a Channex
  // booking behind it can carry an attachment at all, because that is the only send call the vendor
  // integration has. `{ available, reason }` so the composer can say WHY rather than showing a dead
  // control. Defaults to unavailable, so a frontend deployed ahead of the backend simply shows no
  // camera instead of a button that 409s.
  const [photoReply, setPhotoReply] = useState({ available: false, reason: null });
  // Photos chosen but not yet sent, with their object URLs for the preview strip. Local to the
  // composer and never persisted: nothing is uploaded until «إرسال», so closing the composer or
  // switching threads cannot leave an orphaned object in storage (D129).
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const photoInputRef = useRef(null);

  /**
   * Staged photos belong to the thread they were picked in — exactly like the draft, and for the same
   * reason: carrying them to another conversation is how a photo of one unit's parking reaches the
   * wrong guest. Their blobs are released on the way out.
   *
   * Declared HERE, above every handler that calls it, because those handlers name it in their
   * dependency arrays — and a dependency array is evaluated during render, so a definition further
   * down the component would be in the temporal dead zone and throw on first paint.
   *
   * `outgoing` is deliberately NOT cleared alongside this (see `changeFilter`): it holds idempotency
   * keys for anything unresolved, and losing those is how a retry becomes a duplicate.
   */
  const clearStagedPhotos = useCallback(() => {
    setPendingPhotos(prev => {
      prev.forEach(p => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
  }, []);

  // Correlates a thread response to the click that asked for it. Without this, clicking a slow
  // thread then a fast one lets the slow response land last and paint ITS messages under the
  // newer thread's header — one guest's private messages shown under another guest's name.
  const threadRequestId = useRef(0);

  // ---- Inc 5a: unread + ownership ----
  // Told to us by the server on every list response, exactly like `replyEnabled` — there is no
  // REACT_APP_ flag because CRA bakes those at build time. Defaults false, so a frontend deployed
  // ahead of the backend renders the pre-Inc-5 inbox rather than empty badges and dead buttons.
  const [teamStateEnabled, setTeamStateEnabled] = useState(false);
  const [claimBusy, setClaimBusy] = useState(false);
  const [ownershipError, setOwnershipError] = useState(null);
  // Set when a colleague takes the open thread while there is text in the composer. A WARNING, never
  // a lock (D80): the send still goes through, but the collision is visible before Send rather than
  // discovered afterwards by the guest receiving two answers.
  const [collision, setCollision] = useState(null);

  // Read by the ownership-sync effect below, which must not re-run every keystroke — a `draft`
  // dependency would rebuild it on every character typed.
  const draftRef = useRef('');
  // How far the OPEN THREAD's payload read, straight from the server. Echoed back on mark-read so we
  // only ever mark seen what was actually shown — see the thread response's `readCursor`.
  //
  // CARRIES ITS CONVERSATION ID, and that is load-bearing rather than tidy. A bare cursor says
  // nothing about WHICH thread it came from, so in the window between opening thread B and its
  // response arriving, this still held thread A's cursor (or null on the first open of a session) —
  // and the refresh check below would read "there is something newer than what we have shown" about
  // a thread it had never loaded, and fire a redundant refetch during the real one.
  const readCursorRef = useRef({ conversationId: null, cursor: null });
  // Same guard the thread fetch has, for the LIST. Inc 5a gave this three concurrent callers (the
  // interval, visibility-change, and the post-claim refresh) on top of the filter change, and
  // last-wins between them is how a successful claim visibly reverts, or one filter's rows land
  // under another filter's chip.
  const listRequestId = useRef(0);

  // Mobile is a real view switch (list ↔ thread), not the old CSS's stacked panes — the desktop
  // 2-pane layout has no mobile story worth salvaging (D57/D77). Desktop ignores this entirely.
  const [mobileView, setMobileView] = useState('list');

  // ---- Inc 5b: deep link from a push notification ----
  //
  // «tapping opens that exact thread» (D78 — removing one navigation step is a large part of why
  // push was chosen over WhatsApp). The URL is `/#/inbox?c=<conversationId>&n=1`.
  //
  // READ, NEVER WRITTEN. Clicking a row does NOT update the address bar: that would push a history
  // entry per row, so Back would walk the operator through their own reading session instead of
  // leaving the inbox. Deep-linking only needs one direction.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get('c');
  // `n=1` is the ONLY thing that makes an open count as push-driven. Never inferred from an ordinary
  // open — that would turn "notifications work" into "the inbox gets used", which ConversationOpen
  // already answers (D88).
  const cameFromPush = searchParams.get('n') === '1';
  // One auto-open per NAVIGATION, not per conversation id. Without any guard the 30s list refresh
  // would re-open the thread every tick, wiping the draft being typed; but keying on the id ALONE
  // was too coarse — a second notification for a thread the operator had since navigated away from
  // was swallowed, so the app came to the front showing a different conversation with no sign
  // anything had been missed. The whole query string is the key, and the service worker varies it
  // per notification.
  const handledDeepLinkRef = useRef(null);
  const navigationKey = searchParams.toString();
  const [deepLinkMissing, setDeepLinkMissing] = useState(false);

  // ---- Inc 7: context panel ----
  // Arrival evidence + door code for the booking behind the open thread. Told to us by the server
  // on the thread payload, exactly like `replyEnabled` and `teamStateEnabled`, and for the same
  // reason: CRA bakes REACT_APP_ flags at build time, so one could not be flipped without a rebuild.
  const [contextPanelEnabled, setContextPanelEnabled] = useState(false);
  // ---- Pre-booking increment 4 ----
  // The SECOND shape of the same panel, for a thread with no booking. Its own server-side switch,
  // told to us on the same two responses and for the same reason. Read only to decide whether an
  // enquiry thread is worth a context request at all — the panel itself is chosen by the payload's
  // `kind`, never by this flag, so a stale boolean can waste a round trip but can never render the
  // wrong shape.
  const [preBookingPanelEnabled, setPreBookingPanelEnabled] = useState(false);
  // Stage 3.5 Inc 4 — the shadow evaluator's unsent draft. Server-owned switch, like every other
  // capability flag on this page: a CRA env var is baked at build time and could not be flipped
  // without a rebuild, so it would not be a kill switch.
  const [enquiryShadowEnabled, setEnquiryShadowEnabled] = useState(false);
  const [enquiryDraft, setEnquiryDraft] = useState(null);
  // Collapsed by default on EVERY screen. See `SHADOW_COLLAPSED` for why, and why the collapsed
  // line has to carry the prompt rather than a bare chevron.
  const [draftOpen, setDraftOpen] = useState(false);
  const [labelSaving, setLabelSaving] = useState({});
  // `{field, text}` rather than a bare string, so the message renders beside the question that
  // failed rather than at the bottom of a four-row block.
  const [labelError, setLabelError] = useState(null);
  // ⚠️ THE LIVE conversation id, for async guards. A `const` copied from `contextConversationId`
  // inside a closure compares equal to itself forever — which is why the old guard never fired.
  const liveConversationIdRef = useRef(null);
  // The «لماذا؟» chip group, focused once when it appears — see the effect below.
  const unsafeReasonGroupRef = useRef(null);
  // Whether photos render or the thread keeps the honest «مرفق» label. Server-told on the same two
  // responses and for the same reason as the switches above. Defaults FALSE so a frontend deployed
  // ahead of the backend shows the label it always showed, never a column of failed image fetches.
  const [attachmentsEnabled, setAttachmentsEnabled] = useState(false);
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState(false);
  // NOTE: no request-id ref here, unlike the thread and list fetches. The context fetch lives in an
  // EFFECT, whose cleanup React guarantees to run before the next run — so a closure flag already
  // covers the race, and covers unmount too. See the effect for the full reasoning.

  const threadEndRef = useRef(null);
  // Last measured keyboard height, so the effect below can tell "the keyboard just opened" from
  // "the visual viewport moved again while it was already open".
  const kbInsetRef = useRef(0);

  // `silent` refreshes the list WITHOUT the spinner-and-clear cycle: used after a successful send,
  // where blanking the inbox (and wiping it entirely on a transient error) would punish the operator
  // for the one action that just worked.
  const fetchConversations = useCallback(async ({ silent = false } = {}) => {
    const requestId = ++listRequestId.current;
    // Every write below is guarded by this. Without it, a 30s tick issued moments before a claim can
    // land after it and paint the pre-claim owner back — at which point the header flips to
    // «أتولى هذه» and clicking it reports that a colleague took the thread, naming the operator
    // themselves. Same shape as the thread-fetch guard.
    const isStale = () => listRequestId.current !== requestId;

    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await conversationApi.list({
        needsReply: filter === 'needsReply',
        unowned: filter === 'unowned',
        hidden: filter === 'hidden',
        channel: filter === WHATSAPP_FILTER.key || filter === OTA_FILTER.key ? filter : undefined
      });
      if (isStale()) return;
      setConversations(data.conversations || []);
      setTotal(data.pagination?.total ?? (data.conversations || []).length);
      setTeamStateEnabled(!!data.teamStateEnabled);
      setWhatsappInboxEnabled(!!data.whatsappInboxEnabled);
      setWhatsappFeed(data.whatsappFeed || null);
      // Learned at PAGE LOAD, from the list, so the panel can start loading beside the messages on
      // the very first thread open rather than after them. The thread response carries it too, and
      // both write the same boolean — React bails out on an identical value, so this costs nothing.
      setContextPanelEnabled(!!data.contextPanelEnabled);
      setPreBookingPanelEnabled(!!data.preBookingPanelEnabled);
      setEnquiryShadowEnabled(!!data.enquiryShadowEnabled);
      setUnavailable(false);
      // WHICH FILTER THESE ROWS ACTUALLY BELONG TO. `filter` alone cannot answer that: it changes at
      // the moment of the click, while `conversations` still holds the PREVIOUS filter's rows until
      // this line runs. The deep-link effect needs the difference — see its comment.
      setLoadedFilter(filter);
    } catch (err) {
      if (isStale()) return;
      // A list we do not have is not a list that says "the thread isn't there". Cleared so the
      // deep-link effect waits for a real answer rather than concluding "missing" from a failure.
      setLoadedFilter(null);
      // An unmounted route (flag off) and a broken deploy are indistinguishable here — both are a
      // bare HTML 404 — so this ALWAYS logs and the copy never claims the state was intentional.
      if (isEndpointMissing(err)) {
        console.error('Inbox endpoint returned a bare 404 — feature flag off, or the route is not deployed:', err?.config?.url);
        // Only a USER-INITIATED load may declare the feature unavailable. `unavailable` blanks the
        // entire page — filters, list, thread and composer — so letting a background tick set it
        // means one blip during a deploy wipes the screen of someone mid-conversation, which is
        // exactly what `silent` exists to prevent.
        if (!silent) setUnavailable(true);
      } else {
        console.error('Failed to load conversations:', err);
        setError('تعذّر تحميل المحادثات. حاول لاحقًا.');
      }
      // The previous filter's rows must not sit under the newly-active chip — but a SILENT refresh
      // is not a filter change, so a hiccup there must not blank a list the operator is reading.
      if (!silent) {
        setConversations([]);
        setTotal(0);
      }
    } finally {
      // Whichever request is newest ends the spinner, quiet or not. A background refresh overtaking the first load
      // makes that load stale, so leaving this to the loud request alone would spin forever over a list that arrived.
      if (!isStale()) setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Keep the list breathing. Inc 3 accepted "no manual refresh" as a Low, but per-user unread and
  // ownership are TEAM signals: a claim nobody else sees until they reload is not coordination.
  // Paused while the tab is hidden — a backgrounded inbox polling all night is pure cost — and
  // refreshed immediately on return, which is the moment the data is most stale.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') fetchConversations({ silent: true });
    };
    const id = setInterval(tick, LIST_REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [fetchConversations]);

  useEffect(() => { draftRef.current = draft; }, [draft]);

  /**
   * Fold refreshed ownership into the open thread, and raise the collision warning.
   *
   * Depends on `conversations` ALONE. `selected` is reached through the functional updater and
   * returned UNCHANGED when nothing differs — React bails out on an identical reference, so this
   * cannot loop. Adding `selected` or `draft` to the deps would either loop or rebuild the effect on
   * every keystroke.
   */
  // `selected` is read through a ref rather than a dependency: depending on it would re-run this on
  // every fold it performs. The ref is written by the same effect, so it always trails by one render
  // — which is exactly what "what did the previous list say" needs.
  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);
  useEffect(() => { liveConversationIdRef.current = selected?._id ? String(selected._id) : null; }, [selected]);

  // In flight, for the SILENT refresh only. Without it a second list tick arriving before the
  // refetch resolves would start a second one — harmless for correctness (the request-id ticket
  // supersedes the first) but it would re-POST the open marker for the same reading.
  const threadRefreshBusy = useRef(false);
  // Keys currently in flight, readable from an effect without making `sendingKeys` a dependency of
  // it — the same reason `draftRef` exists a few lines up, and mirrored the same way rather than
  // assigned during render (a render-phase write is a side effect, and StrictMode renders twice).
  // Declared BEFORE the fold effect below, so within one commit this is already current when that
  // effect reads it.
  const sendingKeysRef = useRef(sendingKeys);
  useEffect(() => { sendingKeysRef.current = sendingKeys; }, [sendingKeys]);

  // Is a LOUD thread load on screen right now? Mirrored the same way and for the same reason.
  //
  // ⚠️ THE SPINNER TRAP. Only the loud path owns `messagesLoading` — it sets it, and clears it in
  // `finally` only when its own request is still the current one. A silent refresh takes a
  // request-id ticket like everyone else, so one starting mid-load would supersede the loud fetch:
  // the loud one then declines to clear the flag (correctly — it no longer owns the pane) and the
  // silent one never had it. The result is a thread pane stuck on «جارٍ تحميل الرسائل…» over messages
  // that had already arrived, until the operator clicked another row. So the refresh does not start
  // while a load is in flight; it waits one 30s tick, which costs nothing.
  const messagesLoadingRef = useRef(false);
  useEffect(() => { messagesLoadingRef.current = messagesLoading; }, [messagesLoading]);

  /**
   * Load the open thread's messages. Extracted from `openThread` so the SILENT refresh below can
   * reuse it verbatim — one fetch path, so the two can never disagree about what a thread contains.
   *
   * `silent` changes exactly three things and nothing else: no spinner, no error banner, and the
   * messages already on screen survive a failure. Same reasoning as the list's own silent refresh —
   * blanking a thread somebody is reading, to report a transient blip, is worse than the blip.
   */
  const fetchThreadMessages = useCallback(async (conversationId, { silent = false } = {}) => {
    const requestId = ++threadRequestId.current;
    // Every later write is guarded by this: if another row was clicked while we were waiting, this
    // response is stale and must be dropped rather than painted under the newer thread's header.
    const isStale = () => threadRequestId.current !== requestId;

    if (!silent) setMessagesLoading(true);
    try {
      // The endpoint sorts oldest-first, so page 1 is the START of history. Threads are ~10
      // messages, so one request is the whole thread; the second call only ever fires on a thread
      // longer than THREAD_PAGE_SIZE, where the newest page is the one worth showing.
      let data = await conversationApi.messages(conversationId, { limit: THREAD_PAGE_SIZE });
      const pages = data?.pagination?.pages || 1;
      if (pages > 1) {
        if (isStale()) return;
        data = await conversationApi.messages(conversationId, { limit: THREAD_PAGE_SIZE, page: pages });
      }
      if (isStale()) return;
      setMessages(data.messages || []);

      // DROP OUR OWN CONFIRMED SENDS FOR THIS THREAD — the server has just returned them merged, so
      // keeping the local copy renders the same reply twice.
      //
      // This used to live in `openThread`, which meant the SILENT refresh skipped it entirely: send a
      // reply, the guest answers, the refresh runs, and the operator saw their own message twice — in
      // precisely the sequence this feature exists for. The two bubbles render from different lists
      // with different key spaces, so React never warns.
      //
      // Moved here rather than copied: one definition, and it now fires only once the server has
      // ACTUALLY returned them. Doing it before the fetch (as `openThread` did) meant a failed load
      // dropped the bubble for a reply the server had never confirmed.
      //
      // Only `sent`. Anything unresolved — in flight, failed, unconfirmed — stays, because this is
      // the only place its idempotency key lives, and losing that key is how a retype becomes a
      // second message to the guest.
      setOutgoing(prev => {
        const mine = prev[conversationId];
        if (!mine || !mine.some(o => o.status === 'sent')) return prev;   // identity ⇒ no re-render
        return { ...prev, [conversationId]: mine.filter(o => o.status !== 'sent') };
      });

      // A successful load means there is nothing to report. Without this a SILENT refresh that
      // succeeded after a failed open left «تعذّر تحميل الرسائل» on screen — and the render gates the
      // whole message list on that banner, so the messages it had just fetched stayed invisible.
      setMessagesError(null);

      // Read from the response rather than cached once at page load: the flag can be flipped and the
      // backend restarted while a session is open, and the composer must follow it.
      setReplyEnabled(!!data.replyEnabled);
      setWhatsappWindow(data.whatsappWindow || null);
      // Long thread: we show the newest page only. Say so rather than presenting a partial thread
      // (or a lone orphan message, when the last page holds one) as if it were the whole history.
      setOlderMessagesExist(pages > 1);
      setTeamStateEnabled(!!data.teamStateEnabled);
      // Same contract, same reason as the two above — the server owns the switch, and it may be
      // flipped while a session is open. Read here rather than at page load so the panel follows it.
      setContextPanelEnabled(!!data.contextPanelEnabled);
      setPreBookingPanelEnabled(!!data.preBookingPanelEnabled);
      setEnquiryShadowEnabled(!!data.enquiryShadowEnabled);
      // Read HERE only, not at page load: attachments render inside an open thread and nowhere else,
      // so the thread payload is the first moment the answer is needed — and it arrives with the
      // messages it applies to, so a mid-session flip takes effect on the next thread opened.
      setAttachmentsEnabled(!!data.attachmentsEnabled);
      // Whether the camera exists at all for THIS conversation (D126). Per-thread rather than
      // per-session, because it depends on the conversation's own attribution — an enquiry cannot
      // carry a photo while the booking beside it can. Defaulting to unavailable on a payload from an
      // older backend is the safe direction: no camera beats a camera that always fails.
      setPhotoReply({
        available: !!data.photoReply?.available,
        reason: data.photoReply?.reason || null
      });
      // How far THIS payload read. Echoed back below so we mark seen only what was actually shown —
      // anything that arrives between now and the POST stays unread, which is the point. It is also
      // what the silent refresh compares against to decide there is anything new to fetch at all,
      // which is why it is stamped WITH the thread it belongs to.
      readCursorRef.current = { conversationId, cursor: data.readCursor || null };

      // "I have looked at this" — D68, and the open half of the D64 chain.
      // NOT awaited, and its failure is logged rather than surfaced: nothing on screen depends on
      // it, and D61 is explicit that an extra second opening a thread sends staff back to the
      // Airbnb app. The badge clears optimistically because the server's answer would only ever
      // confirm what the operator just did.
      //
      // Safe to repeat on a silent refresh: the read marker is a `$max` (it cannot move backwards)
      // and the open record's unique key is the WAITING PERIOD, so an unchanged episode dedups on
      // E11000 — the designed path — while a genuinely new one records an honest second reaction.
      // Which it is: the operator is looking at the thread as the message lands.
      if (data.teamStateEnabled) {
        conversationApi.markOpen(conversationId, data.readCursor || null)
          .catch(err2 => console.error('Failed to mark conversation as read:', err2));
        setConversations(prev => prev.map(c => (c._id === conversationId ? { ...c, unread: false } : c)));
      }
    } catch (err) {
      if (isStale()) return;
      console.error('Failed to load thread:', err);
      if (!silent) setMessagesError('تعذّر تحميل الرسائل. حاول لاحقًا.');
    } finally {
      if (!silent && !isStale()) setMessagesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!conversations.length) return;
    const prev = selectedRef.current;
    if (!prev) return;

    const fresh = conversations.find(c => c._id === prev._id);
    // Not on this page — e.g. answering a thread under «بانتظار الرد» drops it out of the filter.
    // Leave the header as it stands rather than invent a state for it.
    if (!fresh) return;

    /**
     * A NEW GUEST MESSAGE ARRIVED WHILE THIS THREAD IS OPEN — pull it in.
     *
     * Before this, the 30s tick refreshed the LIST only: the new message appeared in the row preview
     * while the open pane beside it still showed the old history, so an operator sitting inside a
     * thread did not see it until they navigated away and back. Found on the pass-3 walk, and push
     * is what made it obvious — you get told, you open the thread, and it looks unchanged.
     *
     * ⚠️ THIS IS NOT A NEW EFFECT AND NOT A SEVENTH TIMER. It rides the ownership fold, which already
     * runs on exactly the right signal: a freshly-fetched `conversations`. Which effects it races
     * with, per the pass-2 lesson that effect order here is load-bearing:
     *   • the list fetch + 30s tick (declared FIRST) — this is downstream of them and they carry
     *     their own `listRequestId` guard, so nothing is added.
     *   • `openThread` / the deep link (declared LATER) — the refetch takes a `threadRequestId`
     *     ticket, so a row click or a deep-link open supersedes it mid-flight rather than racing it.
     *   • `submitReply` — it captures `threadRequestId.current` and DISCARDS its own result if the
     *     counter moves, so a refresh during a send would strand the bubble on «جارٍ الإرسال»
     *     forever. Hence the in-flight check below: it waits one tick instead.
     *   • scroll-to-end — it fires on any `messages` change, so this must only refetch when there is
     *     genuinely something new, or a reading operator would be yanked to the bottom every 30s.
     *
     * TRIGGERED ON OUR CLOCK (D93), never the provider's. `lastGuestMessageStoredAt` is when WE
     * received it; `lastGuestMessageAt` is the OTA's own stamp, so a late-delivered message would not
     * move it. Read defensively — a frontend deployed ahead of the backend simply does not refresh.
     *
     * GUEST MESSAGES ONLY, deliberately. `lastMessageAt` also moves on our OWN send, which would
     * refetch the thread out from under a bubble the operator just posted. A colleague's PMS reply
     * appearing live is out of scope; the collision banner below already covers the case that matters.
     */
    const storedAt = fresh.lastGuestMessageStoredAt;
    const read = readCursorRef.current;
    if (
      storedAt
      && !threadRefreshBusy.current
      && !messagesLoadingRef.current                 // never supersede a loud load — see the ref
      && sendingKeysRef.current.size === 0
      // Only judge a cursor that belongs to THIS thread. Before its first response lands, the ref
      // still describes whatever was open previously, and comparing against that would fire a
      // refetch during the load that is about to answer the question anyway. A thread whose cursor
      // we do not yet hold is not a thread we can say anything is newer than.
      && read?.conversationId === fresh._id
    ) {
      // No cursor for a thread we HAVE loaded means it had no guest message at all, so any stored-at
      // is new.
      if (!read.cursor || new Date(storedAt) > new Date(read.cursor)) {
        threadRefreshBusy.current = true;
        fetchThreadMessages(fresh._id, { silent: true })
          .finally(() => { threadRefreshBusy.current = false; });
      } else if (isWhatsApp(fresh) && prev.lastMessageAt !== fresh.lastMessageAt) {
        // A reply typed on the ops phone isn't a guest message, but staff reading the chat should still see it.
        threadRefreshBusy.current = true;
        fetchThreadMessages(fresh._id, { silent: true })
          .finally(() => { threadRefreshBusy.current = false; });
      }
    }

    const prevOwnerId = prev.owner?._id ? String(prev.owner._id) : null;
    const nextOwnerId = fresh.owner?._id ? String(fresh.owner._id) : null;
    // A WhatsApp thread's booking link can change while it's open too, and the bar under its header reads it.
    const whatsappStateOf = (c) => JSON.stringify([c.matchedBookingId, c.suggestedBooking?.bookingId, c.guestName, c.lastMessageAt]);
    const whatsappChanged = isWhatsApp(fresh) && whatsappStateOf(prev) !== whatsappStateOf(fresh);
    if (prevOwnerId === nextOwnerId && prev.needsReply === fresh.needsReply && !whatsappChanged) return;

    // Someone ELSE now holds a thread that was free (or mine) while there is text in the composer.
    // Only this transition warns: arriving at an already-owned thread is not a collision, it is
    // information the header already shows.
    //
    // Computed OUT HERE, not inside a setState updater. Updaters must be pure — StrictMode invokes
    // them twice in development, and React may re-run them when rebasing — so a side effect there
    // fires more than once and can re-raise a banner the operator has dismissed.
    const takenByOther = nextOwnerId && nextOwnerId !== String(currentUserId) && nextOwnerId !== prevOwnerId;
    if (takenByOther && draftRef.current.trim()) setCollision(fresh.owner?.name || 'عضو الفريق');

    setSelected(cur => (cur && cur._id === fresh._id
      ? { ...cur, ...(isWhatsApp(fresh) ? fresh : {}), owner: fresh.owner, ownedAt: fresh.ownedAt, needsReply: fresh.needsReply }
      : cur));
  }, [conversations, currentUserId, fetchThreadMessages]);

  const openThread = useCallback(async (conversation) => {
    setSelected(conversation);
    setWhatsappWindow(null);
    setMobileView('thread');
    setMessages([]);
    setMessagesError(null);
    setOlderMessagesExist(false);
    // Translation view belongs to the thread it was toggled in — message ids never collide, but
    // carrying the map across threads would keep it growing for the whole session for no benefit.
    setShowOriginal({});
    // Ownership feedback belongs to the thread it happened in; carrying either across reads as a
    // problem with the thread just opened.
    setOwnershipError(null);
    setCollision(null);
    // A draft belongs to the thread it was typed in — carrying it across would invite sending one
    // guest's reply to another. Staged photos are the same rule, and the sharper version of it: a
    // photo of one unit's parking sent to a different guest is a mistake nobody can un-see.
    setDraft('');
    clearStagedPhotos();
    // Assume no camera until this thread's payload says otherwise — carrying the previous
    // conversation's capability across would offer a control the new thread may not support.
    setPhotoReply({ available: false, reason: null });
    // SAME RULE, AND IT IS THE SHARPEST CASE OF IT. The context effect resets these too, but an
    // effect runs AFTER the commit that already swapped the header to the new guest — so for that
    // commit the panel still held the previous guest's door code and arrival verdict, and rendered
    // them under the new guest's name. Brief on a click; genuinely painted on the deep-link path,
    // where `openThread` is itself called from an effect. Per-thread state is cleared HERE,
    // synchronously, exactly like the five lines above it.
    setContext(null);
    setContextError(false);
    // ⚠️ SYNCHRONOUSLY, for the reason the block above states — and this line was missing, which
    // made it the worst defect on the screen. An effect runs AFTER the commit that already swapped
    // the header, so for the whole of the next thread's round trip the operator saw thread B's
    // messages under thread A's suggested reply, A's unit code and A's highlighted chips. A tap in
    // that window posted A's judgement onto B's case: permanent, silent, and indistinguishable from
    // a real answer in the one dataset the AI decision rests on.
    setEnquiryDraft(null);
    // ...and the state that hangs off it. An in-flight save left its row DISABLED on the next
    // thread, and a stale error line sat under a question nobody had answered there.
    setLabelSaving({});
    setLabelError(null);
    // ⚠️ AND THE OPEN STATE. Carried across a switch, the operator would land on the NEXT thread
    // with a form already covering it — the exact behaviour the collapse exists to remove.
    setDraftOpen(false);
    // NOTE: dropping our own confirmed sends now happens inside `fetchThreadMessages`, on SUCCESS —
    // it used to be here, which meant the silent refresh never did it and rendered the reply twice.
    // The request-id ticket is taken inside — synchronously, before any await — so a click still
    // invalidates anything already in flight, including a silent refresh.
    await fetchThreadMessages(conversation._id);
  }, [fetchThreadMessages, clearStagedPhotos]);

  /**
   * THE CONTEXT PANEL (Inc 7) — arrival evidence + door code for the booking behind the open thread.
   *
   * ⚠️ WHICH EFFECTS THIS RACES WITH (required reading before adding anything else to this file —
   * two separate defects here have come from a later effect reading state an earlier one queued):
   *
   *   • The OWNERSHIP-FOLD effect rebuilds `selected` into a NEW OBJECT whenever owner or
   *     needsReply changes, and the 30s silent list refresh makes that happen on a timer. Keying
   *     this effect on `selected` would therefore refetch the panel every 30 seconds, forever, on
   *     every open thread. It is keyed on the BOOKING ID STRING instead, which is stable across
   *     every one of those folds. This is the specific defect designed against, not a hypothetical.
   *   • `openThread` sets `selected` synchronously and then awaits the messages. This effect fires
   *     on that same render, so the panel and the thread load IN PARALLEL — "the panel never blocks
   *     the thread" is therefore structural, not a promise about ordering.
   *   • The DEEP-LINK effect reaches the thread through `openThread`, so it is the same path as a
   *     click and needs nothing special here.
   *   • The SILENT THREAD REFRESH re-fetches messages for the same conversation. It does not touch
   *     `selected`, so the panel is untouched — correct: neither the door code nor an arrival
   *     verdict changes because a new message arrived.
   *
   * Nothing in the thread render path reads `context`, `contextLoading` or `contextError`, so a
   * failure here cannot delay or break messages.
   */
  // ALL THREE derived as STRINGS, and that is the whole trick: they survive every rebuild of the
  // `selected` object, so the effect below fires once per thread rather than once per list refresh.
  const contextConversationId = selected?._id ? String(selected._id) : null;

  // «رسالة الاطمئنان» strip (tasks/stay-check-message.md D14). The server decides whether it shows
  // (`context.stayCheckPrompt`); here we only act on it. Insert APPENDS — never overwrites what the
  // operator already typed. The strip hides at once; the record is best-effort (a failed save only
  // means the strip may reappear on reload — it never sends anything).
  const actOnStayCheckPrompt = useCallback((action) => {
    if (!contextConversationId) return;
    if (action === 'inserted') {
      setDraft((prev) => (prev.trim() ? `${prev.trimEnd()}\n${STAY_CHECK_PROMPT.readyLine}` : STAY_CHECK_PROMPT.readyLine));
    }
    setContext((prev) => (prev ? { ...prev, stayCheckPrompt: false } : prev));
    conversationApi.stayCheckPrompt(contextConversationId, action)
      .catch((err) => console.warn('stay-check prompt action not saved:', err?.message));
  }, [contextConversationId]);
  const contextBookingId = selected?.matchedBookingId ? String(selected.matchedBookingId) : null;
  // The unit an ENQUIRY was about, which is what the pre-booking shape is built on. Read through
  // `_id || itself` because the field arrives POPULATED today but is a bare ObjectId whenever the
  // identity flag is off on the server — the same defensiveness `unitLabel` applies one screen over.
  const contextEnquiryPropertyId = selected?.enquiryPropertyId
    ? String(selected.enquiryPropertyId._id || selected.enquiryPropertyId)
    : null;

  // Where this guest actually is. The thread header names the unit from the BOOKING, so on a
  // relocated guest it is wrong — and this is the surface where someone types «رمز الدخول ...» or
  // «شقتكم في ...» to the guest themselves. One id, but through the same hook as the arrivals list:
  // one fetch path means the two surfaces cannot answer this differently.
  // ⚠️ Null on an enquiry thread — around half of them — which is a real "no booking", not a
  // failure, so the hook is simply not enabled.
  const { relocations: threadRelocations } = useRelocations(
    contextBookingId ? [contextBookingId] : [],
    Boolean(contextBookingId)
  );
  const threadRelocation = contextBookingId ? threadRelocations[contextBookingId] : null;

  useEffect(() => {
    // IS THERE ANYTHING FOR THE PANEL TO SAY? A booking gives the arrival + door-code shape; a named
    // enquiry unit gives the pre-booking shape. An enquiry we could NOT name has neither (D110), and
    // the client already knows that from the row it is holding — so it costs no round trip to
    // discover a null the endpoint was always going to return.
    const worthFetching =
      !!contextBookingId || (preBookingPanelEnabled && !!contextEnquiryPropertyId);

    if (!contextPanelEnabled || !contextConversationId || !worthFetching) {
      setContext(null);
      setContextError(false);
      // Cleared HERE too. An in-flight request from the previous thread has its `.finally`
      // suppressed by its own cleanup, so without this line a booking thread → enquiry thread
      // switch left the flag stuck true until some later thread happened to clear it.
      setContextLoading(false);
      return undefined;
    }

    // ONE GUARD, NOT TWO. This started with a request-id ref as well, mirroring the thread fetch —
    // but the two are not the same situation. React always runs an effect's cleanup before the next
    // run, so `cancelled` is already true for run N before run N+1 starts: a counter could never
    // catch anything `cancelled` misses, while `cancelled` additionally covers unmount, which a
    // counter does not. The ref was dead weight that a comment credited as load-bearing, which is
    // worse than not having it — the next person to simplify would have removed the wrong one.
    let cancelled = false;
    setContextLoading(true);
    setContextError(false);

    conversationApi.context(contextConversationId)
      .then((data) => {
        if (cancelled) return;
        // `context: null` is a SUCCESS — the thread has no booking, or the booking is gone. The
        // panel renders nothing at all; it is not an error state and must not read as one.
        setContext(data?.context || null);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        // Flag off (403) is expected and silent — the UI has already been told not to render, and
        // logging it would cry wolf on every thread open during a staged rollout.
        // A 404 is a MISSING CONVERSATION, not a failed arrival lookup: surfacing it through the
        // panel's error line would tell the operator we could not verify the guest's entry, which
        // is a claim about the lock. Nothing to show, so show nothing.
        if (status !== 403 && status !== 404) {
          console.error('Failed to load thread context:', err);
          setContextError(true);
        }
        setContext(null);
      })
      .finally(() => {
        if (!cancelled) setContextLoading(false);
      });

    return () => { cancelled = true; };
    // `contextBookingId` and `contextEnquiryPropertyId` are deps in their own right, not just
    // inputs to the guard: either can change WITHOUT the conversation changing — a thread gains a
    // booking the moment a ledger message attributes it, and the enquiry unit is cleared in the same
    // breath — and the panel must follow the switch from one shape to the other.
  }, [
    contextConversationId,
    contextBookingId,
    contextEnquiryPropertyId,
    contextPanelEnabled,
    preBookingPanelEnabled
  ]);

  /**
   * THE UNSENT DRAFT (Stage 3.5 Inc 4). Fetched per thread, exactly like the context panel.
   *
   * ⛔ Display only. There is no send action anywhere in this component, and adding one is
   * Increment 6's job, not a small change to this block.
   *
   * Unlike the context panel there is no client-side "is it worth asking" test: a thread carries a
   * draft or it does not, and the client holds nothing that predicts which. At ~7 enquiries a week
   * the extra round trip is nothing, and guessing wrong would hide the very cases we are collecting.
   */
  useEffect(() => {
    setLabelError(null);
    if (!enquiryShadowEnabled || !contextConversationId) {
      setEnquiryDraft(null);
      return undefined;
    }

    let cancelled = false;
    conversationApi.enquiryDraft(contextConversationId)
      .then((data) => {
        if (cancelled) return;
        // `draft: null` is a SUCCESS — most threads are not enquiries. Render nothing.
        setEnquiryDraft(data?.draft || null);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        // Flag off (403) and a missing conversation (404) are both expected and silent, for the same
        // reasons the context effect documents. Nothing here is worth an error line to an operator:
        // the draft is an internal review aid, not something they are waiting on.
        if (status !== 403 && status !== 404) console.error('Failed to load the suggested reply:', err);
        setEnquiryDraft(null);
      });

    return () => { cancelled = true; };
  }, [contextConversationId, enquiryShadowEnabled]);

  /**
   * Record what the guest was ACTUALLY asking (D160), and whether their words contradict the
   * enquiry's own dates (D149). This is the ground truth the shadow period exists to collect — a
   * model, if one is ever evaluated, is scored against this and never shown before it.
   */
  /**
   * Move focus to the «لماذا؟» group the once, when «لا» reveals it. A new `role="group"` inserted
   * below the caret is otherwise silent to a screen reader and unreachable without Tabbing.
   *
   * ⚠️ AN EFFECT, NOT A CALLBACK REF. A callback ref runs on every render, so every reason tap
   * would re-focus the group and fight the operator. Keyed on the answer, so it fires on the
   * transition into «لا» and not again.
   */
  useEffect(() => {
    if (enquiryDraft?.safeToAutomate === 'no') unsafeReasonGroupRef.current?.focus();
  }, [enquiryDraft?.safeToAutomate]);

  const saveLabel = useCallback(async (patch) => {
    if (!contextConversationId) return;
    // ⚠️ EVERY KEY IN THE PATCH, not `Object.keys(patch)[0]`. A two-key patch locked only its first
    // field, so the second was written by a request nothing guarded: tap a reason, change your mind
    // and tap «نعم» before it lands, and both requests write `unsafeReason` with the loser's value
    // surviving — an objection stored against a draft the operator declared safe, invisible on
    // screen because the reason row is unmounted at «نعم».
    const fields = Object.keys(patch);
    if (fields.some((f) => labelSaving[f])) return;

    // Captured at call time, and compared against a REF rather than the captured value — the old
    // guard compared `contextConversationId` with a const copied from it in the same closure, so it
    // was always false and never fired. A failure on thread A could then paint A's draft, unit code
    // and evaluation time under thread B's guest, and the next chip tapped carried A's version.
    const conversationId = contextConversationId;
    setLabelSaving((f) => ({ ...f, ...Object.fromEntries(fields.map((k) => [k, true])) }));
    setLabelError(null);
    // Optimistic: the operator has already made this judgement, and making them wait on a round
    // trip is friction for nothing. A failure reverts and SAYS SO.
    const previous = enquiryDraft;
    setEnquiryDraft((d) => (d ? { ...d, ...patch } : d));
    try {
      // ⚠️ THE VERSION THEY READ, sent with the answer. The server used to re-resolve "newest case
      // for this thread", so a guest revising their dates between render and tap moved the label
      // onto a question the operator never saw.
      await conversationApi.labelEnquiryDraft(conversationId, { ...patch, enquiryVersion: previous?.enquiryVersion });
    } catch (err) {
      if (liveConversationIdRef.current !== conversationId) return;   // they moved on; nothing to revert into
      // ⚠️ REVERT ONLY THE KEYS THIS CALL CHANGED. Restoring the whole object would stomp a
      // CONCURRENT field's successful save — and two saves in flight is now the normal rhythm
      // (safety, then reason), so the screen would show an answer the server no longer holds.
      setEnquiryDraft((d) => (d ? { ...d, ...Object.fromEntries(fields.map((k) => [k, previous?.[k] ?? null])) } : d));
      // A SILENT FAILURE HERE IS THE FEATURE FAILING. The whole increment rests on these answers,
      // and a chip that lights up and quietly un-lights reads as a mis-tap — so the operator taps
      // again, into the same rate limiter. Translated from the code, per I18N_STRATEGY.
      const code = err?.response?.data?.code;
      // Keyed by field so the message renders beside the question that failed. With four rows now,
      // a single message at the bottom of the block is off-screen on a phone — which is the stated
      // use case — and `role="alert"` only helps a screen reader.
      setLabelError({ field: fields[0], text: SHADOW_SAVE_ERRORS[code] || SHADOW_SAVE_ERRORS.DEFAULT });
      console.error('Failed to save the label:', err);
    } finally {
      setLabelSaving((f) => ({ ...f, ...Object.fromEntries(fields.map((k) => [k, false])) }));
    }
  }, [contextConversationId, labelSaving, enquiryDraft]);

  /**
   * Open the thread a notification pointed at, once the list it lives in has arrived.
   *
   * WHY IT WAITS FOR THE LIST rather than fetching the conversation directly: the thread header
   * renders unit, guest, stay dates and booking reference, all of which come from the LIST
   * projection. Fetching messages alone would paint a thread under an empty header — the surface
   * the Inc 3 audit already found could show one guest's messages under another guest's name.
   *
   * THE FILTER FALLBACK is not theoretical. A notification fires for a guest message, so the thread
   * is at the very top of «الكل» — but the operator may have left the page on «غير مسندة», which
   * excludes it the moment a colleague claims it. Switching to «الكل» once is the difference between
   * opening the thread and telling someone their notification was broken.
   */
  useEffect(() => {
    if (!deepLinkId || unavailable || loading) return;
    if (handledDeepLinkRef.current === navigationKey) return;

    const match = conversations.find((c) => c._id === deepLinkId);

    if (!match) {
      // Not under the active filter — widen once, and let the refetch bring us back here.
      if (filter !== 'all') {
        setFilter('all');
        return;
      }

      // ⚠️ WAIT FOR A LIST THAT ACTUALLY ANSWERS THE QUESTION. `filter === 'all'` is not enough:
      // effects run in declaration order, and the list-refetch effect above is declared FIRST, so on
      // the render right after `setFilter('all')` it has only just called `setLoading(true)` — a
      // state update this closure cannot see. So `loading` still reads false here while
      // `conversations` still holds the PREVIOUS filter's rows. Concluding "missing" from that and
      // latching the ref meant the thread was declared not-found and then never opened when the real
      // «الكل» list arrived a moment later — the exact case this fallback exists for, broken. The
      // same shape swallowed a thread after any failed first load. `loadedFilter` is the honest test:
      // it says which filter these rows were actually fetched under, and is null after a failure.
      if (loadedFilter !== 'all') return;

      // Genuinely not in the first page of «الكل». Rare (a notified thread sorts to the top), but
      // said out loud rather than leaving a tap that silently did nothing.
      handledDeepLinkRef.current = navigationKey;
      setDeepLinkMissing(true);
      return;
    }

    handledDeepLinkRef.current = navigationKey;
    setDeepLinkMissing(false);
    openThread(match);

    // Recorded only once the thread is genuinely open — `openedAt` must mean "they saw it", not
    // "they tapped something we then failed to show". Fire-and-forget: this is measurement, and a
    // metric must never be able to break the navigation that produced it.
    if (cameFromPush) {
      notificationApi
        .recordOpen({ subjectKind: 'conversation', subjectId: deepLinkId, deviceId: getStoredDeviceId() })
        .catch((err) => console.error('Failed to record push-driven open:', err));

      // SPEND THE PUSH MARKER, so it cannot be spent twice. A refresh remounts this component and
      // resets the ref, and the URL still said «I came from a notification» — so F5 re-fired the
      // open. Server-side that cannot re-stamp the notification already claimed; it claims the NEXT
      // OLDER unopened one instead, which is a genuinely missed notification being recorded as
      // opened, with a time-to-open nobody waited. That is the D88 metric corrupting itself.
      //
      // `replace: true` writes no history entry, so decision E-b's reasoning (Back must leave the
      // inbox, not walk through a reading session) is preserved. `c` is deliberately KEPT: a reload
      // should still land on the same thread, just without claiming a notification brought you there.
      const spent = new URLSearchParams(searchParams);
      spent.delete('n');
      spent.delete('t');
      // Claim the post-strip key too, or the resulting re-render reads as a fresh navigation and
      // re-opens the thread.
      handledDeepLinkRef.current = spent.toString();
      setSearchParams(spent, { replace: true });
    }
  }, [deepLinkId, navigationKey, cameFromPush, conversations, loading, unavailable, filter,
      loadedFilter, openThread, searchParams, setSearchParams]);

  /**
   * Send, or retry a failed send.
   *
   * @param {object} [existing] a previously-failed local record — retried with ITS OWN key, which is
   *                            the entire duplicate-send guarantee. A fresh key on retry would make
   *                            the server treat it as a new message and the guest could receive two.
   * @param {boolean} [ownsBusy=true] whether THIS call owns the composer's busy flag. `submitCompose`
   *                            passes false: it holds the flag across the whole text→photos sequence,
   *                            and letting the text half clear it would unlock the composer while
   *                            photos are still uploading — the operator could then fire a second
   *                            compose action into the middle of the first.
   * @returns {Promise<boolean>} did it reach the guest? Added for `submitCompose`, which must not
   *                            post photos into a thread whose text never landed. Every pre-existing
   *                            caller ignores the return, so this is additive.
   */
  const submitReply = useCallback(async (existing, { confirmDuplicate = false, ownsBusy = true } = {}) => {
    if (!selected) return false;
    const text = existing ? existing.message : draft.trim();
    if (!text) return false;

    const key = existing ? existing.key : newIdempotencyKey();
    if (sendingKeys.has(key)) return false;   // this exact send is already in flight
    const conversationId = selected._id;
    // Same guard the thread fetch uses: a slow send must never write its result into a thread the
    // operator has since navigated away from. The counter moves on every thread open and filter change.
    const requestId = threadRequestId.current;

    // Patch one record within its own conversation bucket.
    const patch = (fields) => setOutgoing(prev => ({
      ...prev,
      [conversationId]: (prev[conversationId] || []).map(o => (o.key === key ? { ...o, ...fields } : o))
    }));

    setSendingKeys(prev => new Set(prev).add(key));
    if (existing) {
      patch({ status: 'pending', error: null });
    } else {
      setComposerBusy(true);
      setOutgoing(prev => ({
        ...prev,
        // No local timestamp: the browser clock can be wrong, and a send has no confirmed time until
        // the server reports one. Until then the bubble shows its STATUS instead of a lie.
        [conversationId]: [...(prev[conversationId] || []), { key, message: text, status: 'pending', error: null, at: null }]
      }));
      setDraft('');   // cleared immediately; a failure keeps the text safe in the local record above
    }

    try {
      const data = await conversationApi.reply(conversationId, { message: text, idempotencyKey: key, confirmDuplicate });
      // Navigated away mid-send: report SUCCESS to the caller (the guest has it) while writing
      // nothing into a thread that is no longer on screen.
      if (threadRequestId.current !== requestId) return true;
      patch({ status: 'sent', at: data?.send?.sentAt || null, sentByName: data?.send?.sentByName || null });
      // The thread was answered, so the list's «بانتظار الرد» badge is now stale. Refresh QUIETLY —
      // a normal refetch blanks the list while it loads and wipes it on error, which is a jarring
      // thing to do to someone who is mid-thread and just succeeded.
      fetchConversations({ silent: true });
      return true;
    } catch (err) {
      console.error('Failed to send reply:', err);
      if (threadRequestId.current !== requestId) return false;
      const status = err?.response?.status;
      const body = err?.response?.data;

      if (body?.code === 'WHATSAPP_WINDOW_CLOSED') {
        // WhatsApp's 24h ran out with the tab open. Nothing was sent, so this is a plain failure, and the fresh
        // thread swaps the composer for the notice.
        patch({ status: 'failed', error: body.error });
        fetchThreadMessages(conversationId, { silent: true });
      } else if (status === 409) {
        // Another attempt of THIS key is still in flight. Not a failure and never a re-send: the
        // earlier attempt may already have reached the guest.
        patch({ status: 'pending' });
      } else if (body?.unconfirmed) {
        // The honest middle state: a timeout or a 5xx, so the guest MAY already have it. Offering a
        // plain "retry" here would be the app quietly deciding to risk a duplicate on the operator's
        // behalf — so this renders its own wording and its own explicitly-labelled action.
        patch({ status: 'unconfirmed', error: body?.error || 'تعذّر تأكيد الإرسال' });
      } else if (status === 403) {
        patch({ status: 'failed', error: REPLY_DISABLED });
        setReplyEnabled(false);
      } else {
        patch({ status: 'failed', error: body?.error || 'تعذّر الإرسال' });
      }
      return false;
    } finally {
      setSendingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      if (!existing && ownsBusy) setComposerBusy(false);
    }
  }, [selected, sendingKeys, draft, fetchConversations, fetchThreadMessages]);

  /**
   * Send ONE photo, or retry a previously-failed one (Inc 8).
   *
   * A near-twin of `submitReply` and deliberately a SEPARATE function rather than a branch inside it
   * (D123): the two call different endpoints, carry different payloads, and each owns its own
   * idempotency key. Folding them together would put a `kind` check on every line of the shared body
   * to save a dozen lines of genuinely different logic — and would put the working text path one
   * typo away from a regression.
   *
   * @param {object} record the local outgoing record (created by `submitCompose` or being retried).
   *                        It holds BOTH the File and the key — the key is the whole duplicate-send
   *                        guarantee, so a retry must reuse this record and never build a fresh one.
   * @returns {Promise<boolean>} did it reach the guest? The orchestrator uses this to decide whether
   *                        to continue the sequence.
   */
  const submitPhoto = useCallback(async (record, { confirmDuplicate = false } = {}) => {
    if (!selected || !record?.file) return false;
    const { key, file } = record;
    if (sendingKeys.has(key)) return false;
    const conversationId = selected._id;
    const requestId = threadRequestId.current;

    const patch = (fields) => setOutgoing(prev => ({
      ...prev,
      [conversationId]: (prev[conversationId] || []).map(o => (o.key === key ? { ...o, ...fields } : o))
    }));

    setSendingKeys(prev => new Set(prev).add(key));
    patch({ status: 'pending', error: null });

    try {
      const data = await conversationApi.replyPhoto(conversationId, { file, idempotencyKey: key, confirmDuplicate });
      if (threadRequestId.current !== requestId) return true;
      // Record WHICH stored photo this bubble became, never where it is (D133). The local preview
      // keeps painting until the fetched object lands, so the bubble never blinks; only the stored
      // one survives a refresh, because an object URL dies with the page.
      patch({ status: 'sent', at: data?.send?.sentAt || null, sendId: data?.send?._id || null });
      fetchConversations({ silent: true });
      return true;
    } catch (err) {
      console.error('Failed to send reply photo:', err);
      if (threadRequestId.current !== requestId) return false;
      const status = err?.response?.status;
      const body = err?.response?.data;

      if (status === 409 && body?.code === 'INBOX_PHOTO_UNAVAILABLE') {
        // The conversation cannot carry photos — our capability flag was stale. Correct it, so the
        // camera disappears rather than inviting the same failure again.
        patch({ status: 'failed', error: body?.error || 'لا يمكن إرسال الصور في هذه المحادثة' });
        setPhotoReply({ available: false, reason: body?.reason || null });
      } else if (status === 409) {
        // Another attempt of THIS key is in flight. Never a re-send.
        patch({ status: 'pending' });
      } else if (body?.unconfirmed) {
        patch({ status: 'unconfirmed', error: body?.error || 'تعذّر تأكيد الإرسال' });
      } else if (status === 403) {
        patch({ status: 'failed', error: 'إرسال الصور غير مُفعّل حالياً' });
        setPhotoReply({ available: false, reason: 'disabled' });
      } else {
        patch({ status: 'failed', error: body?.error || 'تعذّر إرسال الصورة' });
      }
      return false;
    } finally {
      setSendingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [selected, sendingKeys, fetchConversations]);

  /**
   * ONE «إرسال» — the compose action the operator actually performs (D124).
   *
   * The UI presents this as a single send. The transport underneath is emphatically NOT one
   * transaction, and this function is where that difference is honestly managed:
   *
   *   • TEXT GOES FIRST, always. It is the mission-critical half, and on the vendor side it is also
   *     what opens the OTA thread — an attachment posted to a thread that was never opened is
   *     rejected outright.
   *   • PHOTOS GO SEQUENTIALLY, stopping at the first failure. Parallel sends would let photo 3 land
   *     before photo 1 and render the conversation out of the order the operator built it.
   *   • A FAILURE NEVER RETRACTS WHAT ALREADY LANDED. If the text reached the guest and a photo did
   *     not, the text stays sent and the photo shows its own retry. Marking the whole action failed
   *     would be a lie about what the guest received, and re-sending the text on retry would post it
   *     twice.
   */
  const submitCompose = useCallback(async () => {
    if (!selected) return;
    const text = draft.trim();
    const photos = pendingPhotos;
    if (!text && !photos.length) return;

    const conversationId = selected._id;

    // Photo records are created up front, so all three bubbles appear the instant «إرسال» is pressed
    // rather than materialising one at a time as the sequence crawls forward. Each carries its own
    // key from birth — that key is what a later retry replays.
    const photoRecords = photos.map(p => ({
      key: newIdempotencyKey(),
      kind: 'photo',
      file: p.file,
      previewUrl: p.previewUrl,
      status: 'queued',
      error: null,
      at: null
    }));

    if (photoRecords.length) {
      setOutgoing(prev => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), ...photoRecords]
      }));
      // Cleared immediately: the Files now live on the outgoing records, and the preview strip must
      // not keep offering to send what is already being sent. The object URLs are revoked when the
      // records are dropped, not here — the bubbles are still rendering them.
      setPendingPhotos([]);
    }

    setComposerBusy(true);
    try {
      if (text) {
        // `ownsBusy: false` — THIS function holds the composer lock for the whole sequence. Letting
        // the text half release it would re-enable Send and the camera while photos were still
        // uploading, so a second compose action could interleave with the first.
        const textDelivered = await submitReply(undefined, { ownsBusy: false });
        // STOP if the text did not land. Not a nicety: the text is what OPENS the OTA thread on the
        // vendor side, and an attachment posted to a thread that was never opened is rejected
        // outright — so continuing would fire N doomed calls and paint N failures for one underlying
        // problem the operator has already been told about. The photos keep their keys and their
        // «إرسال» buttons, so nothing is lost; retrying the text first is the correct order.
        if (!textDelivered) return;
      }
      for (const record of photoRecords) {
        const delivered = await submitPhoto(record);
        // Stop at the first failure. The remaining photos stay 'queued' with their keys intact, so
        // «إعادة المحاولة» resumes exactly where the sequence stopped instead of re-sending what the
        // guest already has.
        if (!delivered) break;
      }
    } finally {
      setComposerBusy(false);
    }
  }, [selected, draft, pendingPhotos, submitReply, submitPhoto]);

  /**
   * Take the operator's picked files into the preview strip.
   *
   * Nothing is uploaded here — the bytes stay in the browser until «إرسال». That is what makes the
   * abandoned-upload problem structurally impossible rather than something a sweep has to clean up
   * afterwards (D129).
   */
  const onPhotosPicked = useCallback((e) => {
    const picked = Array.from(e.target.files || []);
    // Reset immediately, or picking the SAME file twice in a row fires no change event and the
    // operator thinks the control is broken.
    e.target.value = '';
    if (!picked.length) return;

    setPendingPhotos(prev => {
      const room = MAX_REPLY_PHOTOS - prev.length;
      if (room <= 0) return prev;
      const added = picked.slice(0, room).map(file => ({
        // Not the file name: two photos from a camera roll can share one, and this is a React key.
        id: `${file.name}-${file.size}-${file.lastModified}-${prev.length}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        previewUrl: URL.createObjectURL(file)
      }));
      return [...prev, ...added];
    });
  }, []);

  /** Drop a photo before it is sent, releasing its preview so the blob is not held for the session. */
  const removePendingPhoto = useCallback((id) => {
    setPendingPhotos(prev => {
      const target = prev.find(p => p.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(p => p.id !== id);
    });
  }, []);

  // Photos left staged when the operator switches thread or leaves the page would otherwise hold
  // their blobs for the life of the session. Revoked on the way out; the send path hands its object
  // URLs to the outgoing records, which outlive this state deliberately.
  useEffect(() => () => {
    pendingPhotos.forEach(p => URL.revokeObjectURL(p.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Claim / release / supervisor release — one handler, because all three are the same shape: call,
   * take the server's ownership state as the truth, refresh the list quietly so colleagues' rows
   * update too.
   *
   * Nothing optimistic here, deliberately. A claim can legitimately LOSE (a colleague was a moment
   * faster), and painting it as won before the server agrees would show two people the same thread
   * as theirs — the precise confusion ownership exists to prevent. The mirror of the reply path,
   * where optimism IS right because the send is already committed.
   */
  const changeOwnership = useCallback(async (action) => {
    if (!selected || claimBusy) return;
    const conversationId = selected._id;
    // Compare-and-set for the supervisor override: release the owner we were LOOKING AT, so a claim
    // made in the last few seconds cannot be yanked by a click aimed at somebody else.
    const expectedOwnerUserId = selected.owner?._id ? String(selected.owner._id) : null;
    setClaimBusy(true);
    setOwnershipError(null);
    try {
      const data = action === 'forceRelease'
        ? await conversationApi.forceRelease(conversationId, expectedOwnerUserId)
        : await conversationApi[action](conversationId);
      setSelected(prev => (prev && prev._id === conversationId
        ? { ...prev, owner: data.owner || null, ownedAt: data.ownedAt || null }
        : prev));
      setCollision(null);
      // So the rest of the team sees it without waiting for their own 30s tick.
      fetchConversations({ silent: true });
    } catch (err) {
      console.error(`Failed to ${action} conversation:`, err);
      const body = err?.response?.data;
      // A 409 carries WHO won, so the losing click can name them instead of showing a bare failure.
      if (body?.owner) {
        setSelected(prev => (prev && prev._id === conversationId
          ? { ...prev, owner: body.owner, ownedAt: body.ownedAt || null }
          : prev));
      }
      setOwnershipError(body?.error || 'تعذّر تحديث حالة الاستلام');
    } finally {
      setClaimBusy(false);
    }
  }, [selected, claimBusy, fetchConversations]);

  const onComposerKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    // `isComposing` matters for Arabic and any IME: pressing Enter to accept a candidate must not
    // fire the message.
    if (e.nativeEvent?.isComposing) return;
    // On a touch keyboard Enter ALWAYS breaks the line and never sends — the send button is the
    // deliberate action there. Desktop keeps the convention the ops team already has in its
    // fingers: Enter sends, Shift+Enter breaks the line.
    if (touchKeyboard || e.shiftKey) return;
    e.preventDefault();
    // The compose action, not the text half of it — Enter must send a staged photo too, or the
    // keyboard path and the button path would post different things from the same composer.
    submitCompose();
  }, [submitCompose, touchKeyboard]);

  // Switching filter changes what the list means, so anything opened from the previous list is
  // stale context — including on mobile, where the chips sit outside the pane switch and tapping
  // one while reading a thread would otherwise refetch an invisible list and appear to do nothing.
  const changeFilter = useCallback((key) => {
    threadRequestId.current++;      // abandon any thread fetch still in flight
    setFilter(key);
    setSelected(null);
    setWhatsappWindow(null);
    setMessages([]);
    setMessagesError(null);
    setOlderMessagesExist(false);
    setShowOriginal({});
    setMobileView('list');
    setOwnershipError(null);
    setCollision(null);
    // A draft belongs to the thread it was typed in; leaving it behind on a filter change is how it
    // ends up sent to a different guest. `outgoing` deliberately survives — it holds the idempotency
    // keys of anything unresolved, and losing those is how a retype becomes a duplicate message.
    setDraft('');
    clearStagedPhotos();
    // A new list means a new thread will be opened; until its payload says otherwise, assume no
    // camera. Stale-available is the one direction that produces a failure the operator can see.
    setPhotoReply({ available: false, reason: null });
  }, [clearStagedPhotos]);

  /**
   * Mobile: leave the thread and go back to the list.
   *
   * ⚠️ IT MUST CLOSE THE THREAD, NOT JUST HIDE IT. This used to be `setMobileView('list')` alone,
   * which left `selected` pointing at a conversation the CSS had set to `display:none`. Harmless
   * while nothing re-read it — but the refresh above reads exactly that, so on the primary push
   * surface a guest message would arrive, get silently fetched into a hidden pane, get marked READ,
   * and record a `ConversationOpen` timed within 30s of arrival. The unread dot would never appear
   * and the D88 open metric would be credited to a human who was looking at a list.
   *
   * Mirrors `changeFilter` exactly, minus the filter: `outgoing` deliberately survives (it holds the
   * idempotency keys of anything unresolved), the draft does not — re-opening clears it anyway.
   */
  const closeThreadOnMobile = useCallback(() => {
    threadRequestId.current++;      // abandon any thread fetch still in flight
    setSelected(null);
    setWhatsappWindow(null);
    setMessages([]);
    setMessagesError(null);
    setOlderMessagesExist(false);
    setShowOriginal({});
    setMobileView('list');
    setOwnershipError(null);
    setCollision(null);
    setDraft('');
    clearStagedPhotos();
    setPhotoReply({ available: false, reason: null });
  }, [clearStagedPhotos]);

  // Land on the newest message, the way every messaging app does. `outgoing` is in the deps too, so
  // a reply the operator just sent scrolls itself into view instead of landing below the fold.
  useEffect(() => {
    if (messages.length || Object.keys(outgoing).length) threadEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, outgoing]);

  // HOW MUCH OF THE SCREEN THE KEYBOARD IS COVERING.
  // On a phone the thread is a viewport-anchored layer with the composer docked to its bottom edge
  // (InboxPage.css, mobile block). In an iOS standalone PWA the LAYOUT viewport does not shrink when
  // the keyboard opens — only the VISUAL one does — so a composer pinned to `bottom: 0` would sit
  // behind the keys the moment anyone tapped it. `visualViewport` is the only thing that reports the
  // covered height, so it is measured here and published for the layer to subtract.
  // A no-op wherever the API is absent, which is also where the layer is not fixed to begin with.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const apply = () => {
      const covered = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      document.documentElement.style.setProperty('--inbox-kb-inset', `${covered}px`);
      // Only on the OPENING edge, never on every event. The thread body shrinks by exactly this
      // much, and a scroll container that loses height from the bottom leaves its newest lines
      // hidden — so the thread is pulled back to the end once. Re-running it on every visual-viewport
      // scroll would drag the view back down while the operator is reading further up.
      if (covered > 0 && kbInsetRef.current === 0) threadEndRef.current?.scrollIntoView({ block: 'end' });
      kbInsetRef.current = covered;
    };

    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      // Left behind, a stale inset would sit on the document element for every other page in the app.
      document.documentElement.style.removeProperty('--inbox-kb-inset');
    };
  }, []);

  const renderListRow = (c) => {
    const stay = stayLabel(c);
    const isSelected = selected?._id === c._id;
    // PER-USER (D68). A colleague opening this thread does not clear it here — team-level unread
    // evaporates on shift handover, which is exactly what this avoids.
    const isUnread = teamStateEnabled && c.unread;
    const ownerName = c.owner?.name;
    const ownedByMe = isOwnedByMe(c.owner);
    return (
      <button
        key={c._id}
        type="button"
        className={`inbox-row${isSelected ? ' is-selected' : ''}${c.needsReply ? ' needs-reply' : ''}${isUnread ? ' is-unread' : ''}`}
        onClick={() => openThread(c)}
      >
        <div className="inbox-row-top">
          {/* The dot carries the meaning for anyone who cannot perceive the weight change, so it
              gets the accessible label rather than relying on bolder text alone. */}
          {isUnread && <span className="inbox-unread-dot" role="img" aria-label="غير مقروءة" />}
          {/* Name + «متكرر» travel together, so the badge sits beside the name rather than being
              pushed to the time at the row's far end. */}
          <span className="inbox-row-name">
            <span className="inbox-row-guest"><ChannelLogo conversation={c} />{guestLabel(c)}</span>
            <RepeatGuestBadge repeatGuest={c.repeatGuest} />
          </span>
          <time className="inbox-row-time">{formatRelativeTime(c.lastMessageAt)}</time>
        </div>

        <div className="inbox-row-preview">
          {/* Our own last message is prefixed, mirroring how staff read the Airbnb row. */}
          {/* Name the colleague who replied when the provider told us — «أنت» is plainly wrong when
              someone else on the team answered, and today every reply is sent from the OTA app by a
              named person. Falls back to «أنت» only when the stamp is missing. */}
          {c.lastMessageSender === 'property' && (
            <span className="inbox-row-prefix">{c.lastMessageOutsidePms ? 'من خارج النظام' : (c.lastMessageSenderName || 'أنت')}: </span>
          )}
          {c.lastMessagePreview
            || (isWhatsApp(c) && c.lastMessageType ? whatsappTypeLabel(c.lastMessageType) : null)
            || <span className="inbox-row-muted">لا توجد رسائل</span>}
        </div>

        <div className="inbox-row-meta">
          {c.currentlyHosting && (
            <span className="inbox-hosting">
              <span className="inbox-hosting-dot" aria-hidden="true" />
              مستضيف حالياً
            </span>
          )}
          {contactLine(c).length > 0 && <span className="inbox-row-unit">{contactLine(c)}</span>}
          {stay && <span className="inbox-row-stay">{stay}</span>}
        </div>

        {/* NOT linked on the row, deliberately. The whole row is a <button> that opens the thread —
            nesting an <a> inside it is invalid HTML, and its click would fight the row's own, so a
            tap meant to open the conversation could land on the booking page instead. The code is a
            link in the thread header, where it stands alone and the intent is unambiguous. */}
        <div className="inbox-row-ref">{bookingRef(c, { linked: false })}</div>

        {/* Read-only here on purpose. The whole row is a <button>, so nesting a claim control inside
            it would be invalid HTML and its click would fight the row's own — a tap meant to open
            the conversation could instead claim it. The ACTION lives in the thread header, where
            the intent is unambiguous; the row only reports the state (D56 — a column, not a second
            application). */}
        {teamStateEnabled && c.owner && (
          <span className={`inbox-owner-chip${ownedByMe ? ' is-mine' : ''}`}>
            <i className="bi bi-person-fill" aria-hidden="true" />{' '}
            {ownedByMe
              ? 'أنت تتابعها'
              : ownerName
                ? <>يتابعها: <bdi>{ownerName}</bdi></>
                : 'يتابعها: عضو غير معروف'}
          </span>
        )}

        {c.needsReply && <span className="inbox-badge-needs-reply">بانتظار الرد</span>}
      </button>
    );
  };

  const renderThread = () => {
    if (!selected) {
      return (
        <div className="inbox-thread-empty">
          <i className="bi bi-chat-left-text" aria-hidden="true"></i>
          <p>اختر محادثة لعرض الرسائل</p>
        </div>
      );
    }

    const stay = stayLabel(selected);
    const ownerId = selected.owner?._id ? String(selected.owner._id) : null;
    const ownedByMe = isOwnedByMe(selected.owner);

    return (
      <>
        <header className="inbox-thread-header">
          <button
            type="button"
            className="inbox-back"
            onClick={closeThreadOnMobile}
            aria-label="رجوع إلى قائمة المحادثات"
          >
            {/* Chevron points along the RTL back direction. */}
            <i className="bi bi-chevron-right" aria-hidden="true"></i>
          </button>
          <div className="inbox-thread-title">
            <span className="inbox-thread-guest-line">
              <span className="inbox-thread-guest"><ChannelLogo conversation={selected} />{guestLabel(selected)}</span>
              <RepeatGuestBadge repeatGuest={selected.repeatGuest} linked />
            </span>
            <span className="inbox-thread-sub">
              {contactLine(selected, stay)}
            </span>
            {/* Linked here: the header is not itself clickable, so the code can be what an operator
                expects it to be — the way through to the booking. */}
            {bookingRef(selected, { linked: true })}
          </div>

          {/* WHO IS ON THIS THREAD, and the only place it can be changed. Three states, three
              affordances — never a dropdown of colleagues: ownership here is SELF-claim, mirroring
              the alerts pattern (D56/D80). Nobody hands a thread to somebody else; a supervisor can
              only free one. */}
          {teamStateEnabled && (
            <div className="inbox-owner-actions">
              {!ownerId && (
                <button type="button" className="inbox-claim-btn" onClick={() => changeOwnership('claim')} disabled={claimBusy}>
                  {claimBusy ? <Spinner animation="border" size="sm" /> : <i className="bi bi-person-check" aria-hidden="true" />}
                  {' '}أتولى هذه
                </button>
              )}

              {ownedByMe && (
                <>
                  <span className="inbox-owner-chip is-mine">
                    <i className="bi bi-person-fill" aria-hidden="true" /> أنت تتابعها
                  </span>
                  <button type="button" className="inbox-release-btn" onClick={() => changeOwnership('release')} disabled={claimBusy}>
                    <i className="bi bi-person-dash" aria-hidden="true" /> إلغاء الاستلام
                  </button>
                </>
              )}

              {ownerId && !ownedByMe && (
                <>
                  {/* `name: null` means owned by an account we can no longer resolve — a deleted
                      user. Saying so plainly is what lets a supervisor recognise it and clear it;
                      rendering it as unowned would offer a claim that can only ever 409. */}
                  <span className="inbox-owner-chip">
                    <i className="bi bi-person-fill" aria-hidden="true" />{' '}
                    {selected.owner?.name
                      ? <>يتابعها: <bdi>{selected.owner.name}</bdi></>
                      : 'يتابعها: عضو غير معروف'}
                  </span>
                  {/* D81: ownership never expires on its own — a thread waiting on a guest must not
                      quietly become unowned. But it must not stay locked to someone who has
                      finished their shift either, so a supervisor can free it, and the action is
                      logged with who took it.
                      NOT «تحرير»: this product uses تحرير for EDITING throughout, so labelling its
                      one destructive action with the word for "edit" invites a misclick. */}
                  {isSupervisor && (
                    <button type="button" className="inbox-release-btn" onClick={() => changeOwnership('forceRelease')} disabled={claimBusy}>
                      <i className="bi bi-person-x" aria-hidden="true" /> سحب الاستلام
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </header>

        {/* ABOVE the context panel, and not inside it: the panel is behind a feature flag and
            renders nothing on an enquiry thread, and this must not be able to disappear with it.
            It is the last thing between an operator and typing the wrong apartment to the guest.
            No ErrorBoundary of its own — unlike the panel it renders no fetched shape, only two
            names it has already null-checked. */}
        <RelocationBanner relocation={threadRelocation} compact className="inbox-relocation" />

        {isWhatsApp(selected) && (
          // Keyed by chat: search results or a half-confirmed hide must never carry over to another guest's chat.
          <WhatsAppThreadBar
            key={selected._id}
            conversation={selected}
            isSupervisor={isSupervisor}
            onChanged={() => {
              fetchConversations({ silent: true });
              fetchThreadMessages(selected._id, { silent: true });
            }}
            onLeftList={() => {
              closeThreadOnMobile();
              fetchConversations({ silent: true });
            }}
          />
        )}

        {/* Inc 7. Directly under the header because it IS header material — the two facts an
            operator would otherwise open the booking page to find. It renders null on an enquiry
            thread, while the panel is loading, and whenever the flag is off, so nothing here
            reserves space it might not use. Nothing below reads its state: a panel failure cannot
            delay or break the messages. */}
        {/* Its own boundary, with a QUIET inline fallback. The fetch was already isolated — nothing
            below reads the panel's state — but the RENDER was not: a throw in here would have hit
            the app-level boundary and replaced the whole inbox, taking the conversation, the
            composer and any unsent draft with it. The default full-screen fallback would be almost
            as bad inside a 32px strip, hence the inline one. */}
        {contextPanelEnabled && (
          <ErrorBoundary
            fallback={
              <div className="inbox-context inbox-context-error" role="status">
                <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                <span>تعذّر عرض معلومات الحجز. المحادثة أدناه غير متأثرة.</span>
              </div>
            }
          >
            {/* `conversationId` is what resets the pre-booking shape's disclosure state between two
                enquiry threads — see the panel: it is not unmounted on a thread switch, and the
                booking id it already keys on is null for every enquiry. */}
            <ThreadContextPanel
              context={context}
              loading={contextLoading}
              error={contextError}
              conversationId={contextConversationId}
            />
          </ErrorBoundary>
        )}

        {ownershipError && <Alert variant="warning" className="inbox-alert inbox-owner-alert">{ownershipError}</Alert>}

        {/* WARN, DO NOT BLOCK (D80). The send below still works: ownership coordinates people, it is
            not a lock, and refusing a reply because a colleague claimed the thread would leave the
            guest waiting on an argument between two staff members. Showing it BEFORE Send is the
            whole point — after the fact, the guest has already had two answers. */}
        {collision && (
          <Alert variant="info" className="inbox-alert inbox-collision" dismissible onClose={() => setCollision(null)}>
            <i className="bi bi-info-circle" aria-hidden="true" />{' '}
            {/* Phrased so the VERB agrees with المحادثة, not with the colleague. «تولّى X» would be
                wrong for every female member of the team, and `User` carries no gender to agree
                with — the sibling «يتابعه: X» sidesteps it the same way. */}
            هذه المحادثة الآن مع <bdi>{collision}</bdi> — يمكنك الإرسال، لكن تحقّق أولاً حتى لا يصل ردّان.
          </Alert>
        )}

        {/* FAULT CONTAINMENT SITS AROUND THE WORKFLOW, NOT THE WIDGET.

            The unit protected here is the MESSAGE DISPLAY, and the composer below is deliberately
            OUTSIDE it — already a sibling in the markup, so no restructuring was needed. That split
            is the entire point: the most valuable thing on this screen is not a message we can
            refetch, it is the half-written reply in the box. An operator who loses three paragraphs
            because a photo would not decode has suffered the real failure; the photo has not.

            Deliberately NOT wrapped per photo. A boundary should be a recovery unit, and a single
            image is not one — that habit ends with a wrapper around every renderer we ever add
            (PDF, video, voice note, location card), which is defensive programming by accumulation.
            The booking panel above set this precedent and this follows it.

            ⚠️ It is a LAST resort, not part of normal control flow, and it deliberately cannot catch
            the failure most likely to occur: a corrupt image does not raise a React error, it fails
            to decode. That is handled where it happens — see MessageAttachment's onError. */}
        <ErrorBoundary
          fallback={
            <div className="inbox-thread-body">
              <div className="inbox-thread-error" role="status">
                <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                <span>تعذّر عرض الرسائل. يمكنك متابعة كتابة ردّك بالأسفل، أو فتح محادثة أخرى.</span>
              </div>
            </div>
          }
        >
        <div className="inbox-thread-body">
          {messagesLoading && (
            <div className="inbox-thread-loading">
              <Spinner animation="border" size="sm" /> جارٍ تحميل الرسائل…
            </div>
          )}

          {messagesError && <Alert variant="danger" className="inbox-alert">{messagesError}</Alert>}

          {!messagesLoading && !messagesError && olderMessagesExist && (
            // Read-only stage: there is no "load older" yet, so the honest thing is to say the
            // view is partial rather than let a truncated thread read as the whole conversation.
            <p className="inbox-history-gap">تُعرض أحدث الرسائل فقط — الرسائل الأقدم غير معروضة</p>
          )}

          {!messagesLoading && !messagesError && messages.length === 0 && (
            <p className="inbox-thread-none">لا توجد رسائل في هذه المحادثة</p>
          )}

          {!messagesLoading && !messagesError && messages.map((m) => {
            // Airbnb's own Arabic rendering of a foreign-language guest message (D114). Present on
            // ~95% of the messages that need one, null on everything else — including every message
            // we sent. Null means "nothing to toggle", never an error.
            const hasTranslation = !!m.translatedMessage;
            const original = showOriginal[m._id];
            // ARABIC BY DEFAULT — the decision, and the reason the condition reads this way round.
            const bodyText = hasTranslation && !original ? m.translatedMessage : m.message;
            return (
            // `side` is decided by the server from the canonical sender helpers. Branching on the
            // raw sender here would be a second definition — and would put the guest's own words
            // in our bubble whenever the provider sends 'Guest', or nothing at all.
            <div key={m._id} className={`inbox-msg is-${m.side || 'system'}`}>
              {/* Who answered, on OUR side only. The guest is already named in the thread header, so
                  repeating it on every inbound bubble adds noise; what staff cannot see today is
                  which colleague replied — and with co-hosts on a thread that is the open question.
                  Sits outside the bubble so it reads as a byline, not as message content. */}
              {m.side === 'us' && m.senderName && (
                <span className="inbox-msg-author">
                  {m.senderName}
                  {m.senderRole === 'COHOST' && ' · مضيف مشارك'}
                </span>
              )}
              <div className="inbox-bubble">
                {/* Automated sends name WHICH automation (D112) — «رسالة تسجيل الدخول» tells an
                    operator something «رسالة تلقائية» alone does not, and this bubble sits in the
                    guest's conversation where the alternative reading is "a colleague wrote this".
                    The generic label stays for any other system-sender the provider gives us. */}
                {m.side === 'system' && (
                  <span className="inbox-msg-auto">
                    {m.automated ? automatedLabel(m.automatedType) : 'رسالة تلقائية'}
                    {m.automated && m.automatedType?.startsWith('review_') && m.senderName && ` · ${m.senderName}`}
                  </span>
                )}
                {/* Plain interpolation — React escapes it. See the file header (G3). The translation
                    is provider text about attacker-influencable content and is escaped identically.
                    `dir="auto"` because Airbnb guests write in English constantly: inside a dir="rtl"
                    pane a Latin message has its trailing punctuation thrown to the wrong edge. `auto`
                    lets the FIRST strong character decide, per message, so Arabic and English are
                    both shaped correctly without us guessing the language — which is exactly what
                    makes it right for a bubble that switches language on a toggle. */}
                {bodyText && <p className="inbox-bubble-text" dir="auto">{bodyText}</p>}
                {/* OUR OWN photo, sent by the check-in automation — an S3 asset we control, so it
                    renders inline. This is NOT the D73 gap: that is inbound guest photos, which the
                    provider holds and we still cannot display (the label below still says so). */}
                {m.automated && m.photoUrl && (
                  <img className="inbox-auto-photo" src={m.photoUrl} alt="صورة الوحدة المُرسلة للضيف" loading="lazy" />
                )}
                {/* A PHOTO A COLLEAGUE SENT (Inc 8). NOT the same as the automated photo above,
                    though the first cut treated it as one: that is a property asset under a public
                    storage prefix, so a URL renders; this is guest-conversation content under a
                    private one, so the browser is given an ID and fetches the bytes through the
                    authenticated API. See SentPhoto.jsx (D133). */}
                {!m.automated && m.replyPhotoId && (
                  <SentPhoto conversationId={selected._id} sendId={m.replyPhotoId} />
                )}
                {/* THE TRANSLATION IS LABELLED, ALWAYS (D114). An operator acting on a machine
                    translation must know that is what they are reading — and the original is one tap
                    away, never buried. The stored record is untouched either way: this switches what
                    is DISPLAYED, nothing else. */}
                {hasTranslation && (
                  <div className="inbox-translation">
                    <span className="inbox-translation-tag">
                      <i className="bi bi-translate" aria-hidden="true" />{' '}
                      {original ? 'النص الأصلي كما كتبه الضيف' : 'ترجمة آلية من المنصة'}
                    </span>
                    <button
                      type="button"
                      className="inbox-translation-toggle"
                      onClick={() => setShowOriginal((prev) => ({ ...prev, [m._id]: !prev[m._id] }))}
                    >
                      {original ? 'عرض الترجمة' : 'عرض النص الأصلي'}
                    </button>
                  </div>
                )}
                {m.haveAttachment && (
                  // THE PHOTO ITSELF — or the D73 label, and WHICH ONE IS DECIDED BY MOUNTING.
                  //
                  // ⚠️ The switch chooses whether the fetching component EXISTS, not merely how it
                  // behaves. Rendering it always and letting a prop suppress the request would gate
                  // the network call and nothing else — a render, hook or lifecycle fault would run
                  // regardless, so "it is behind a flag" would be false reassurance rather than a
                  // kill switch. Off ⇒ this subtree is byte-identical to the pre-build inbox.
                  //
                  // The platform in the label is named from the conversation's own channel: this
                  // inbox serves Booking.com and unattributed threads too, so a hardcoded "Airbnb"
                  // would misdirect staff.
                  attachmentsEnabled
                    ? <MessageAttachment
                        conversationId={selected._id}
                        messageId={m._id}
                        count={m.attachmentCount}
                        fallbackLabel={attachmentHint(selected)}
                      />
                    : <AttachmentLabel text={attachmentHint(selected)} />
                )}
                {m.whatsappFile && attachmentsEnabled && (
                  <WhatsAppFile
                    conversationId={selected._id}
                    messageId={m._id}
                    file={m.whatsappFile}
                    fallbackLabel={whatsappTypeHint(m.messageType)}
                  />
                )}
                {/* A shared location arrives as `link` with the map address as its text, so it needs no label. */}
                {m.messageType && !(m.whatsappFile && attachmentsEnabled) && !m.haveAttachment && !m.replyPhotoId && (WHATSAPP_TYPE_LABELS[m.messageType] || !m.message) && (
                  <AttachmentLabel text={whatsappTypeHint(m.messageType)} />
                )}
              </div>
              {/* Time and delivery state on ONE line beneath the bubble, never inside it.
                  D71's marker used to be the bubble's first line, where it read as the opening words
                  of the message — an operator scanning a thread saw our reply begin with a sentence
                  the guest never received. It belongs where every messaging app puts a receipt: on
                  the meta line, beside the timestamp.

                  ⚠️ ONLY THE UNCONFIRMED CASE IS SHOWN (Adil, 2026-08-02 — reverses the Inc 4 display
                  decision; the RECORD is untouched). «أُرسلت من النظام» rendered under every single
                  PMS reply, and it is on track to mean nothing: once staff actually adopt the inbox —
                  which is the entire point of the D64 window — every reply is sent from here, so a
                  badge on all of them distinguishes nothing. Repeating it on each bubble is the noise
                  D72 warns teaches people to stop reading the screen.

                  What survives is the case that is ACTIONABLE and rare: we could not confirm the OTA
                  took it, so the guest may never have received it. `sentByUserId` / `sentByName` /
                  `channexMessageId` are still written on every send — provenance (D70) and the D64
                  measurement substrate live in `guest_reply_sends`, never in a label. The author
                  byline above the bubble still says WHO replied. */}
              <div className="inbox-msg-meta">
                <time className="inbox-msg-time">{formatShortDateTime(m.sourceInsertedAt)}</time>
                {m.sentFromPms && m.deliveryState !== 'sent' && (
                  <span className="inbox-msg-pms">أُرسلت من النظام — لم يتم تأكيد الوصول</span>
                )}
                {m.sentOutsidePms && <span className="inbox-msg-pms">من خارج النظام</span>}
              </div>
            </div>
            );
          })}
          {/* Replies made in this view, painted immediately. Same bubble shape as a real message —
              only the status line underneath differs — so a reply never looks like a different kind
              of thing from the conversation it joins. */}
          {(outgoing[selected._id] || []).map((o) => (
            <div
              key={o.key}
              className={`inbox-msg is-us${o.status === 'failed' ? ' is-failed' : ''}${o.status === 'unconfirmed' ? ' is-unconfirmed' : ''}`}
            >
              <div className="inbox-bubble">
                {/* A photo record has no text — the image IS the message (Inc 8). `previewUrl` is the
                    local blob, shown from the instant «إرسال» is pressed; once the server confirms,
                    `sendId` lets the same bubble fetch the STORED object, which is the one that
                    survives a refresh. Before that it is null and SentPhoto simply shows the
                    preview. */}
                {o.kind === 'photo'
                  ? <SentPhoto conversationId={selected._id} sendId={o.sendId} previewUrl={o.previewUrl} />
                  : <p className="inbox-bubble-text" dir="auto">{o.message}</p>}
              </div>
              {/* Same meta line as a ledger message, so a reply never looks like a different kind of
                  thing from the conversation it joins. */}
              <div className="inbox-msg-meta">
                {o.status === 'pending' && <span className="inbox-msg-status">جارٍ الإرسال…</span>}
                {/* QUEUED — a photo behind one that has not finished, or behind one that FAILED and
                    stopped the sequence. It carries its own send action rather than resuming
                    automatically off the retry above: after a failure the operator may well decide
                    this photo is no longer wanted, and a queue that restarts itself would take that
                    decision away. Rare by construction — it needs two photos AND a mid-sequence
                    failure — so the simple explicit control is the right cost. */}
                {o.status === 'queued' && (
                  <span className="inbox-msg-status">
                    في الانتظار…
                    <button
                      type="button"
                      className="inbox-retry"
                      onClick={() => submitPhoto(o)}
                      disabled={sendingKeys.has(o.key) || composerBusy}
                    >
                      إرسال
                    </button>
                  </span>
                )}
                {/* Only ever the SERVER's timestamp. Before it arrives there is nothing true to show,
                    and a browser clock is not a substitute for one.
                    The success marker is gone from here for the same reason it is gone from the
                    merged bubble above (Adil, 2026-08-02): a badge that will sit under every reply
                    once the inbox is adopted distinguishes nothing. This bubble is ALREADY known to
                    be ours — the operator typed it seconds ago — so it was the least informative
                    place the marker appeared. */}
                {o.status === 'sent' && (o.at
                  ? <time className="inbox-msg-time">{formatShortDateTime(o.at)}</time>
                  : <span className="inbox-msg-status">تم الإرسال</span>)}
                {o.status === 'failed' && (
                  // Visible failure with a retry, never a silent one — the condition D74 attached to
                  // shipping enquiry replies before the booking-less case is proven in the wild.
                  // Safe to offer a plain retry: this state means Channex DEFINITIVELY rejected it, so
                  // nothing reached the guest.
                  <span className="inbox-msg-status is-failed">
                    <i className="bi bi-exclamation-triangle" aria-hidden="true"></i>
                    {o.error ? ` ${o.error}` : (o.kind === 'photo' ? ' لم تصل الصورة' : ' لم تصل الرسالة')}
                    <button
                      type="button"
                      className="inbox-retry"
                      // Routed by KIND, because the two halves are different endpoints (D123). The
                      // record's own key is replayed either way — that is what makes the retry safe.
                      onClick={() => (o.kind === 'photo' ? submitPhoto(o) : submitReply(o))}
                      disabled={sendingKeys.has(o.key)}
                    >
                      إعادة المحاولة
                    </button>
                  </span>
                )}
                {o.status === 'unconfirmed' && (
                  // A timeout or a server error: the guest MAY already have this message. The app must
                  // not decide to risk a duplicate on the operator's behalf, so there is no plain
                  // "retry" here — the action says exactly what it will do, and only that action tells
                  // the server it may send again.
                  <span className="inbox-msg-status is-unconfirmed">
                    <i className="bi bi-question-circle" aria-hidden="true"></i>
                    {o.error ? ` ${o.error}` : ' تعذّر تأكيد الإرسال'}
                    <button
                      type="button"
                      className="inbox-retry"
                      onClick={() => (o.kind === 'photo'
                        ? submitPhoto(o, { confirmDuplicate: true })
                        : submitReply(o, { confirmDuplicate: true }))}
                      disabled={sendingKeys.has(o.key)}
                    >
                      إرسال نسخة أخرى
                    </button>
                  </span>
                )}
              </div>
            </div>
          ))}

          {/* ═══ THE SUGGESTED REPLY — Stage 3.5 Inc 4, SHADOW MODE ═══
              ⛔ THIS WAS NEVER SENT AND CANNOT BE. It sits at the end of the thread, greyed and
              explicitly labelled, so ops judge it where the guest's words are — there is no review
              queue, because at ~7 cases a week a queue nobody opens teaches nothing (D145).
              Visually distinct from the `is-system` automated bubble on purpose: an operator must
              never be able to mistake this for something that went out.

              Gated on the SAME two flags as every sibling in this pane. Without that it rendered
              alone under a loading spinner during a thread switch, and stayed on screen when the
              messages themselves had failed — a draft with no conversation to judge it against. */}
          {!messagesLoading && !messagesError && enquiryDraft && (
            <section className="inbox-shadow-draft" aria-label="الرد المقترح — لم يُرسل">
              {/* The whole block is one disclosure. Collapsed it is a single line that still says
                  there is work to do; expanded it is what it always was. */}
              <button
                type="button"
                className="inbox-shadow-head"
                aria-expanded={draftOpen}
                aria-controls="inbox-shadow-body"
                onClick={() => setDraftOpen((o) => !o)}
              >
                <i className="bi bi-eye-slash" aria-hidden="true" />
                <span>{SHADOW_COLLAPSED.title}</span>
                {/* ⚠️ THE STATE OF THE WORK, not just a chevron. An unlabelled case has to look like
                    a task on a screen the operator is scrolling past — the labels are the shadow
                    period, and this product has already lost one dataset to a field nobody was
                    prompted to fill. */}
                {enquiryDraft.safeToAutomate && enquiryDraft.intentLabel
                  ? <span className="inbox-shadow-state">{SHADOW_COLLAPSED.labelled}</span>
                  : <span className="inbox-shadow-state is-todo">{SHADOW_COLLAPSED.needsLabel}</span>}
                {enquiryDraft.propertyCode && <span className="inbox-shadow-unit">{enquiryDraft.propertyCode}</span>}
                <i className={`bi ${draftOpen ? 'bi-chevron-up' : 'bi-chevron-down'} inbox-shadow-caret`} aria-hidden="true" />
                <span className="visually-hidden">{draftOpen ? SHADOW_COLLAPSED.collapse : SHADOW_COLLAPSED.expand}</span>
              </button>

              {draftOpen && (
              <div id="inbox-shadow-body">

              {/* The draft, or the REASON there is none — and the reason is the more informative
                  half, so it is shown rather than the block being hidden (D170). */}
              {enquiryDraft.text ? (
                <>
                  <p className="inbox-shadow-text" dir="auto">{enquiryDraft.text}</p>
                  {/* ⚠️ SAYS WHAT THE DRAFT ACTUALLY IS, because the first six prod cases proved the
                      old framing misleading: five of six were a correct availability answer to a
                      question about a discount, a viewing, a late checkout or parking. Until the
                      scope assessment exists (blocked on the API credential) the gate composes this
                      sentence from stored facts alone and never reads what the guest wrote — so the
                      screen must not imply it did. */}
                  <p className="inbox-shadow-note">{SHADOW_DRAFT_NOTE}</p>
                </>
              ) : (
                <p className="inbox-shadow-silent">
                  لم يكن النظام ليرد على هذه الرسالة{enquiryDraft.reason ? ` — ${shadowReasonAr(enquiryDraft.reason)}` : ''}
                </p>
              )}

              {/* D184 — would the same message still have been correct at the moment we would have
                  sent it? Four states, and «لم تتم إعادة الفحص بعد» is NOT «بقي صحيحاً». */}
              <div className="inbox-shadow-meta">
                <time dateTime={enquiryDraft.evaluatedAt}>{formatShortDateTime(enquiryDraft.evaluatedAt)}</time>
                {enquiryDraft.overnight && <span className="inbox-shadow-tag">{SHADOW_OVERNIGHT_TAG}</span>}
                {/* ⚠️ STABILITY ONLY WHERE SOMETHING CAN GO STALE (D197). A stored fact — parking,
                    wifi, the standard check-in hour — cannot change between evaluation and send, and
                    a REFUSED question never had an answer to re-check; for both, «لم تتم إعادة الفحص
                    بعد» would sit there forever reading as a gap in the measurement rather than a
                    category that has none. `null` is the pre-D187 case, and every one of those IS an
                    availability draft — which is why it still shows. */}
                {enquiryDraft.tier !== 'fact' && enquiryDraft.tier !== 'none' && (
                  enquiryDraft.stabilityUnverifiable
                    ? <span className="inbox-shadow-tag">{SHADOW_STABILITY.unverifiable}</span>
                    : enquiryDraft.verdictHeld === true
                      ? <span className="inbox-shadow-tag is-ok">{SHADOW_STABILITY.held}</span>
                      : enquiryDraft.verdictHeld === false
                        ? <span className="inbox-shadow-tag is-warn">{SHADOW_STABILITY.broke}</span>
                        : <span className="inbox-shadow-tag">{SHADOW_STABILITY.pending}</span>
                )}
              </div>

              {/* THE POINT OF THE WHOLE SHADOW PERIOD (D160). After 30–50 of these we know whether
                  simple rules read the guest's Arabic well enough — and if they do, no AI is bought.
                  Each row is a `role="group"` with its own question as the accessible name, and each
                  chip reports `aria-pressed`: without that a screen reader hears ten unlabelled
                  buttons with no indication of which answer is selected (pattern: the filter chips
                  further down this file). */}
              <div className="inbox-shadow-label">
                {/* ⭐ ASKED FIRST, and above the intent label on purpose. The six-case finding is that
                    intent does not map onto safety: a discount, a viewing and a late-checkout request
                    are three different intents and one identical verdict. This asks the go-live
                    question (D146) directly instead of inferring it from a taxonomy.

                    ⚠️ CHIPS ARE NEVER `disabled` WHILE SAVING, and that is an accessibility fix
                    rather than a preference: focus on an element that becomes disabled drops to
                    `<body>`, so a keyboard operator had to Tab from the top of the page after every
                    answer. The re-entrancy guard lives in `saveLabel`, which is where it belongs —
                    it now locks EVERY key in the patch, not just the first. */}
                {/* ⚠️ THE QUESTION CHANGES WITH THE CASE. Most cases are a SILENCE, and asking "was it
                    appropriate to SEND this reply?" when nothing was drafted is unanswerable — the
                    operator has to guess which way round it means, on the one field the go-live
                    decision rests on. The stored VALUES are identical either way; only the words move. */}
                <span className="inbox-shadow-label-q" id="shadow-q-safe">
                  {enquiryDraft.text ? SHADOW_QUESTIONS.safe : SHADOW_QUESTIONS.safeSilent}
                </span>
                <div className="inbox-shadow-chips" role="group" aria-labelledby="shadow-q-safe">
                  {(enquiryDraft.text ? SHADOW_SAFE_TO_AUTOMATE : SHADOW_SAFE_TO_AUTOMATE_SILENT).map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`inbox-shadow-chip${enquiryDraft.safeToAutomate === value ? ' is-on' : ''}`}
                      aria-pressed={enquiryDraft.safeToAutomate === value}
                      aria-busy={!!labelSaving.safeToAutomate}
                      // ⚠️ ONE KEY ONLY. Sending `unsafeReason: null` alongside was redundant with
                      // the server — which now nulls it whenever the RESULTING verdict is not «لا» —
                      // and it was the sole cause of a two-key patch whose second field no guard
                      // covered.
                      onClick={() => saveLabel({ safeToAutomate: value })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {labelError?.field === 'safeToAutomate' && <p className="inbox-shadow-error" role="alert">{labelError.text}</p>}

                {/* Only when the answer is «لا» — an unasked question with no answer is noise, and
                    these five buckets ARE the improvement backlog, not a taxonomy. The group takes
                    focus when it appears: a new `role="group"` inserted below the caret is otherwise
                    silent to a screen reader and invisible to a keyboard operator. */}
                {enquiryDraft.safeToAutomate === 'no' && (
                  <>
                    <span className="inbox-shadow-label-q" id="shadow-q-unsafe">{SHADOW_QUESTIONS.unsafeWhy}</span>
                    <div
                      className="inbox-shadow-chips"
                      role="group"
                      aria-labelledby="shadow-q-unsafe"
                      tabIndex={-1}
                      ref={unsafeReasonGroupRef}
                    >
                      {(enquiryDraft.text ? SHADOW_UNSAFE_REASONS : SHADOW_SHOULD_HAVE_ANSWERED_REASONS).map(({ value, label }) => (
                        <button
                          key={value}
                          type="button"
                          className={`inbox-shadow-chip${enquiryDraft.unsafeReason === value ? ' is-on' : ''}`}
                          aria-pressed={enquiryDraft.unsafeReason === value}
                          aria-busy={!!labelSaving.unsafeReason}
                          onClick={() => saveLabel({ unsafeReason: value })}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {labelError?.field === 'unsafeReason' && <p className="inbox-shadow-error" role="alert">{labelError.text}</p>}
                  </>
                )}

                {/* THE POINT OF THE WHOLE SHADOW PERIOD (D160). After 30–50 of these we know whether
                    simple rules read the guest's Arabic well enough — and if they do, no AI is bought.
                    Each row is a `role="group"` with its own question as the accessible name, and each
                    chip reports `aria-pressed`: without that a screen reader hears ten unlabelled
                    buttons with no indication of which answer is selected (pattern: the filter chips
                    further down this file). */}
                <span className="inbox-shadow-label-q" id="shadow-q-intent">{SHADOW_QUESTIONS.intent}</span>
                <div className="inbox-shadow-chips" role="group" aria-labelledby="shadow-q-intent">
                  {SHADOW_INTENTS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`inbox-shadow-chip${enquiryDraft.intentLabel === value ? ' is-on' : ''}`}
                      aria-pressed={enquiryDraft.intentLabel === value}
                      aria-busy={!!labelSaving.intentLabel}
                      onClick={() => saveLabel({ intentLabel: value })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {labelError?.field === 'intentLabel' && <p className="inbox-shadow-error" role="alert">{labelError.text}</p>}

                {/* ⛔ REVEALED ONLY AFTER THE OPERATOR HAS ANSWERED, and this placement is the whole
                    point. Three of the classifier's fourteen labels are byte-identical to chips in
                    this very block — «توفر», «غير واضح», «خارج النطاق» — so showing its reading first
                    would hand the operator the answer to the question we are asking them. The
                    Increment 5 review would then compute model-versus-human agreement against a
                    human label the model supplied, and read a false pass on the one dataset the
                    buy/no-buy decision rests on. It is D181's "never shown before the human answers"
                    applied to the second model. Shown afterwards it is useful: a wrong reading beside
                    the operator's own is exactly the finding worth noticing. */}
                {enquiryDraft.intentLabel && enquiryDraft.intent && (
                  <p className="inbox-shadow-note">{shadowScopeLine(enquiryDraft.intent)}</p>
                )}

                {/* D149/D183 — collected, never inferred, and NEVER an input to the safety gate in
                    this stage. The operator answers first; a model, if ever evaluated, is scored
                    against this. */}
                <span className="inbox-shadow-label-q" id="shadow-q-contradiction">{SHADOW_QUESTIONS.contradiction}</span>
                <div className="inbox-shadow-chips" role="group" aria-labelledby="shadow-q-contradiction">
                  {SHADOW_CONTRADICTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`inbox-shadow-chip${enquiryDraft.humanContradiction === value ? ' is-on' : ''}`}
                      aria-pressed={enquiryDraft.humanContradiction === value}
                      aria-busy={!!labelSaving.humanContradiction}
                      onClick={() => saveLabel({ humanContradiction: value })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {labelError?.field === 'humanContradiction' && <p className="inbox-shadow-error" role="alert">{labelError.text}</p>}
              </div>
              </div>
              )}
            </section>
          )}

          <div ref={threadEndRef} />
        </div>
        </ErrorBoundary>

        {/* The composer — OUTSIDE the boundary above, on purpose. See its note: a rendering failure
            in the thread must never take the operator's unsent draft with it. */}
        {isWhatsApp(selected) && selected.hiddenAt ? (
          <footer className="inbox-readonly-bar">
            <i className="bi bi-eye-slash" aria-hidden="true"></i>
            <span>هذه المحادثة مخفية. أرجعها أولاً للرد من هنا.</span>
          </footer>
        ) : isWhatsApp(selected) && whatsappWindow && !whatsappWindow.open ? (
          // Meta's rule, not ours: free text only within 24h of the guest's last message.
          <footer className="inbox-readonly-bar">
            <i className="bi bi-clock" aria-hidden="true"></i>
            <span>لا توجد رسالة من الضيف خلال آخر 24 ساعة، فلا يمكن إرسال نص حر. للرد الآن استخدم جوال العمليات.</span>
          </footer>
        ) : replyEnabled ? (
          <form
            className="inbox-composer"
            onSubmit={(e) => { e.preventDefault(); submitCompose(); }}
          >
            {/* «رسالة الاطمئنان» — team-only note, a full row above the field (like staged photos).
                Not a popup and never sent by itself (tasks/stay-check-message.md D14). */}
            {context?.stayCheckPrompt && (
              <div className="inbox-staycheck" role="note">
                <i className="bi bi-info-circle inbox-staycheck-icon" aria-hidden="true"></i>
                <span className="inbox-staycheck-text">{STAY_CHECK_PROMPT.note}</span>
                <button
                  type="button"
                  className="inbox-staycheck-insert"
                  onClick={() => actOnStayCheckPrompt('inserted')}
                  disabled={composerBusy}
                >
                  {STAY_CHECK_PROMPT.insert}
                </button>
                <button
                  type="button"
                  className="inbox-staycheck-dismiss"
                  onClick={() => actOnStayCheckPrompt('dismissed')}
                  aria-label={STAY_CHECK_PROMPT.dismiss}
                  title={STAY_CHECK_PROMPT.dismiss}
                >
                  <i className="bi bi-x" aria-hidden="true"></i>
                </button>
              </div>
            )}
            {/* STAGED PHOTOS — chosen, not yet sent. Above the field rather than inside it: on a
                phone the field is already the narrowest surface in the product, and thumbnails
                sharing that line would push the text input to a sliver. */}
            {pendingPhotos.length > 0 && (
              <div className="inbox-composer-photos">
                {pendingPhotos.map(p => (
                  <div key={p.id} className="inbox-composer-photo">
                    <img src={p.previewUrl} alt="صورة مختارة" />
                    <button
                      type="button"
                      className="inbox-composer-photo-remove"
                      onClick={() => removePendingPhoto(p.id)}
                      disabled={composerBusy}
                      aria-label="إزالة الصورة"
                    >
                      <i className="bi bi-x" aria-hidden="true"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Send sits INSIDE the field, anchored to its trailing edge, the way every messaging app
                the ops team already uses draws it. As a separate control beside the field it cost
                ~54px of a phone's width — on the narrowest reply surface we have — and read as a
                second, unrelated thing to aim at. The wrapper exists so the button is positioned
                against the FIELD and not against the form, whose padding carries the safe-area
                inset: anchoring to the form would have made the button drift up the notch. */}
            <div className="inbox-composer-field">
              {/* THE CAMERA — mounted only where a photo can actually be sent (D126).
                  Hiding it is prevention, not concealment: the alternative is an operator taking a
                  photo of a parking space and only then learning this conversation cannot carry one.

                  NO `capture` ATTRIBUTE, deliberately. Adding it locks the control to the camera;
                  leaving it off makes the OS offer camera AND library in one tap, which is exactly
                  what was asked for and is the same picker the crew already uses on /my-tasks. */}
              {photoReply.available && (
                <>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    style={{ display: 'none' }}
                    onChange={onPhotosPicked}
                    disabled={composerBusy}
                  />
                  <button
                    type="button"
                    className="inbox-composer-camera"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={composerBusy || pendingPhotos.length >= MAX_REPLY_PHOTOS}
                    aria-label={pendingPhotos.length >= MAX_REPLY_PHOTOS ? `الحد الأقصى ${MAX_REPLY_PHOTOS} صور` : 'إرفاق صورة'}
                    title={pendingPhotos.length >= MAX_REPLY_PHOTOS ? `الحد الأقصى ${MAX_REPLY_PHOTOS} صور` : 'إرفاق صورة'}
                  >
                    <i className="bi bi-camera" aria-hidden="true"></i>
                  </button>
                </>
              )}
              <textarea
                className="inbox-composer-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onComposerKeyDown}
                placeholder="اكتب ردك…"
                rows={1}
                // `auto` ONLY once there is something to judge. `dir="auto"` means "infer the
                // direction from the content" — with an empty field there is no content, so the
                // question has no answer and each engine guesses differently: Chrome landed RTL,
                // iPhone Safari landed LTR and put the Arabic placeholder on the wrong edge
                // (reported 2026-08-02). We are not guessing here — an empty composer in an
                // Arabic-first product is RTL, so we say so, and hand back to `auto` the moment
                // there IS content to infer from. Staff answering an English guest still get their
                // reply shaped correctly from the first character they type.
                dir={draft ? 'auto' : 'rtl'}
                aria-label="نص الرد"
                disabled={composerBusy}
              />
              <button
                type="submit"
                className="inbox-composer-send"
                // A photo with no words is a complete message (D125), so Send must be live when
                // either half is present — gating on `draft` alone would make the camera useless
                // unless the operator also typed something.
                disabled={composerBusy || (!draft.trim() && pendingPhotos.length === 0)}
                aria-label="إرسال"
              >
                {composerBusy
                  ? <Spinner animation="border" size="sm" />
                  /* Arrow points along the RTL send direction, matching the back chevron above. */
                  : <i className="bi bi-send" aria-hidden="true"></i>}
              </button>
            </div>
          </form>
        ) : (
          // The kill switch's visible state — the Inc 3 bar, kept for exactly this. An empty band
          // under the last message reads as a composer that failed to load and sends staff hunting
          // for one that is not there.
          <footer className="inbox-readonly-bar">
            <i className="bi bi-lock" aria-hidden="true"></i>
            <span>{REPLY_DISABLED} — {isWhatsApp(selected) ? 'للرد استخدم جوال العمليات' : 'للرد افتح المحادثة في تطبيق المنصة'}</span>
          </footer>
        )}
      </>
    );
  };

  return (
    <DesktopLayout activeNavItem="inbox" headerTitle="صندوق الرسائل">
      {/* The same one piece of state the pane switch runs on, published one level higher as well:
          the page's own header and filter chips sit BEFORE `.inbox-shell` in the DOM, so no sibling
          selector can reach them from there, and on a phone they are what was eating the screen the
          thread needed. One attribute, no second source of truth. */}
      <Container fluid className="inbox-page" dir="rtl" data-mobile-view={mobileView}>
        <div className="inbox-page-header">
          <div className="inbox-page-title">
            <i className="bi bi-chat-dots inbox-page-title-icon" aria-hidden="true"></i>
            <h2>صندوق الرسائل</h2>
            {/* The server's total, not the rendered count — otherwise the badge asserts the page
                size as the backlog and reads "100" forever. */}
            {!loading && !unavailable && total > 0 && (
              <span className="inbox-count-badge">{total}</span>
            )}
          </div>
          <p className="inbox-page-subtitle">
            رسائل الضيوف من منصات الحجز{whatsappInboxEnabled && ` و${getPlatformDisplayName('whatsapp')}`}، في مكان واحد — مع إمكانية الرد مباشرة.
          </p>
        </div>

        {unavailable ? (
          // Wording is "unavailable", never "not enabled yet": this state is also what a broken
          // deploy looks like, and telling staff it was intentional stops anyone escalating it.
          <Alert variant="secondary" className="inbox-alert">
            صندوق الرسائل غير متاح حالياً. إذا استمر ذلك، أبلغ الدعم.
          </Alert>
        ) : (
          <>
            <div className="inbox-filters" role="group" aria-label="تصفية المحادثات">
              {(whatsappInboxEnabled ? [...FILTERS, OTA_FILTER, WHATSAPP_FILTER, ...(isSupervisor ? [HIDDEN_FILTER] : [])] : FILTERS).map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`inbox-chip${filter === f.key ? ' is-active' : ''}`}
                  onClick={() => changeFilter(f.key)}
                  aria-pressed={filter === f.key}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {error && <Alert variant="danger" className="inbox-alert">{error}</Alert>}

            {whatsappFeed && whatsappFeed.state !== 'ok' && whatsappFeed.state !== 'off' && (
              <Alert variant="warning" className="inbox-alert inbox-wa-feed">
                {whatsappFeed.state === 'failing'
                  ? 'تعذّر حفظ رسائل واتساب الواردة. تابع المحادثات من جوال العمليات وأبلغ الدعم.'
                  : 'لم يصل شيء من واتساب منذ فترة. تابع المحادثات من جوال العمليات حتى نتأكد من الربط.'}
              </Alert>
            )}

            {/* A notification whose thread is not on this page. Rare — a notified thread sorts to
                the top — but a tap that appears to do nothing is exactly what teaches people to
                stop tapping. */}
            {deepLinkMissing && (
              <Alert variant="warning" className="inbox-alert" dismissible onClose={() => setDeepLinkMissing(false)}>
                لم نعثر على المحادثة المطلوبة في هذه القائمة. ابحث عنها بالأسفل.
              </Alert>
            )}

            <div className="inbox-shell" data-mobile-view={mobileView}>
              <aside className="inbox-list-pane">
                {loading ? (
                  <div className="inbox-list-loading">
                    <Spinner animation="border" size="sm" /> جارٍ التحميل…
                  </div>
                ) : conversations.length === 0 ? (
                  <p className="inbox-list-empty">لا توجد محادثات</p>
                ) : (
                  <>
                    {conversations.map(renderListRow)}
                    {/* Never let rows go missing quietly — the hidden ones are the OLDEST, i.e.
                        exactly what the needs-reply filter exists to surface. */}
                    {total > conversations.length && (
                      <p className="inbox-list-overflow">
                        يتم عرض أحدث {conversations.length} محادثة من أصل {total}.
                        {' '}استخدم عوامل التصفية لتضييق النتائج.
                      </p>
                    )}
                  </>
                )}
              </aside>

              <section className="inbox-thread-pane">{renderThread()}</section>
            </div>
          </>
        )}
      </Container>
    </DesktopLayout>
  );
};

export default InboxPage;
